import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type CSSProperties,
  type ReactNode,
} from 'react';
import { flushSync } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AnnotBar, type AnnotTool, Button, Tooltip, cn, useToast } from '@vd/ui';
import { highlights, type HighlightColor } from '@vd/tokens';
import {
  NATURAL_H,
  NATURAL_W,
  ORIGINAL_IMAGE_ID,
  clamp,
  clampPinTip,
  clearImageShapes,
  hasEditableShapes,
  isRenderablePinShape,
  undoTargetIndex,
  drawShape,
  onBugSpriteReady,
  normalizeShape,
  pinDisplayNumber,
  pinVisualBounds,
  rasterizeAnnotatedImage,
  rebaseShapes,
  shapeHit,
  shapeId,
  snapshotValueEqual,
  type RectShape,
  type PinShape,
  type Shape,
} from '@vd/annotation-core';
import { ZoomIn, ZoomOut, Maximize2, Plus, Pencil, Trash2, Move, Settings2 } from 'lucide-react';
import type { Attachment, LiveEvent, Ticket } from '@vd/types';
import { useShortcutMap } from '../../lib/keyboard';
import { TextBoxModal, type TextStyle, type TextVariant } from './TextBoxModal';
import { ImageCarousel, type StageImage } from './ImageCarousel';
import { cancelledDragShape, dragBaseChanged, topmostHitShape } from './annotationSelection';
import {
  acknowledgePendingAnnotations,
  persistPendingAnnotations,
  readPendingAnnotations,
  recoverPendingAnnotations,
  retireIncorporatedPendingAnnotations,
  type PendingAnnotationRecord,
} from './annotationPending';
import { enqueueAnnotationPersistence } from './annotationPersistenceQueue';
import { saveAnnotationSnapshot } from './annotationSave';
import { withAnnotationRow } from './annotationCache';
import { receiveServerAnnotations, remoteAnnotationUpdate } from './annotationLive';
import {
  answeredWithError, forgetSent, observeServerVersion, ownSaveBasis, ownSavesKey, recordAnswered, recordSent,
} from './annotationOwnSaves';
import { AnnotationRasterContextRegistry, rasterSource } from './annotationRasterContext';
import { annotationWarningRegistry } from './annotationWarningRegistry';
import { api } from '../../lib/api';
import { captureAuthSession } from '../../lib/authSessionEpoch';
import { useAuth } from '../auth/AuthProvider';
import { markInBox, type ClickMarks } from '../ticket/clickMarks';
import styles from './AnnotationCanvas.module.css';

/**
 * Something the ticket can show in the image's place: today the walkthrough
 * clip (t-cnb0zy). The canvas does not know what it is. It puts `tile` in the
 * image row just after the capture and, while `cover` is set, lays the cover
 * over the image: over all of it when `hidesImage`, otherwise as a bar along
 * its foot. The canvas stays mounted underneath either way, so a drawing
 * part-way through is never lost. Choosing an image, or a drawing tool while
 * the image is hidden, calls `onShowImage`.
 */
export interface StageMedia {
  tile: ReactNode;
  cover: { node: ReactNode; hidesImage: boolean } | null;
  onShowImage: () => void;
}

export interface AnnotationCanvasProps {
  ticketId: string;
  screenshotUrl?: string;
  /**
   * Server-side annotation snapshot for this ticket. Comes from the
   * Ticket payload (`ticket.annotations[0]?.snapshot`). When set, the
   * canvas hydrates from this rather than localStorage — so a teammate
   * opening the ticket sees the same shapes the original author drew,
   * not a blank canvas. localStorage stays as a fallback for any
   * pre-existing data that pre-dates the server-sync feature, and as
   * an offline buffer if the server is unreachable.
   */
  initialShapes?: unknown;
  /**
   * Rendered on the right side of the carousel row, opposite the image
   * thumbnails. Used by the ticket detail page to float the capture-
   * health note (partial / failed) inline with the captures instead of
   * taking up a full banner above the drawing area.
   */
  carouselRightSlot?: ReactNode;
  /** Shown in the image's place on request; see `StageMedia`. */
  stageMedia?: StageMedia;
  /**
   * Server-persisted extra carousel images from the Ticket payload
   * (TOADO-261). The canvas seeds the carousel from these, reconciles
   * teammate additions/removals on refetch, and uploads anything that
   * exists only locally (new adds + pre-migration localStorage extras).
   */
  attachments?: Attachment[];
  /**
   * The hydrated annotation row's version (Ticket payload), seeding
   * the live-event ordering guard.
   */
  initialAnnotationVersion?: number;
  /** Clicks to number on the screenshot (TOADO-919), from `clickMarksFor`. */
  clickMarks?: ClickMarks | null;
}

// The Shape union, normalizeShape, rebaseShapes, hit testing, canvas
// rendering, and raster compositing live in @vd/annotation-core (PRD §7)
// so every annotation surface shares one shape model. This file keeps
// only the web editor: React state, pointer handling, zoom/pan, and the
// selection overlays.

const MIN_ZOOM = 0.1;
const MAX_ZOOM = 4;
/**
 * Matches `.scroll { padding }` — kept in sync for fit / zoom math.
 * The padding gives pan slack when the image is zoomed past the viewport.
 * Was 160px historically, but at fit-zoom that 320px/axis subtraction
 * meant the image rendered at maybe 40% of its natural size on a typical
 * ticket layout — visibly too small. 32px is plenty of slack for normal
 * panning and leaves the image large on first load.
 */
const PAN_PAD = 32;

const DEFAULT_TEXT_STYLE_KEY = 'vd-text-defaults';

type RectDragKind = 'move' | 'resize-tl' | 'resize-tr' | 'resize-bl' | 'resize-br';
type AnnotationDrag =
  | {
      shapeType: 'rect';
      id: string;
      kind: RectDragKind;
      startNatX: number;
      startNatY: number;
      startShape: RectShape;
      startBaseShape?: Shape;
      startVersion: number;
    }
  | {
      shapeType: 'pin';
      id: string;
      kind: 'move';
      startNatX: number;
      startNatY: number;
      startShape: PinShape;
      startBaseShape?: Shape;
      startVersion: number;
    };

// Module-level, NOT refs (review round 8): an upload outlives its
// component. Navigating to an uncached ticket unmounts the canvas, and
// a remount's fresh refs lost the in-flight and removed-while-uploading
// markers - re-opening the ticket and removing the image inside one
// upload round trip then resurrected it. Ids are globally unique
// (12-char base36), and both sets self-clean when their request
// settles, so sharing them across mounts is safe.
const uploadsInFlight = new Set<string>();
const removedWhileUploading = new Set<string>();
const pendingPersistFailures = new Set<string>();
interface AnnotationRasterContext {
  images: StageImage[];
  screenshotUrl?: string;
}

function isSupersededRasterError(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { status?: unknown }).status === 409;
}

interface TextDefaults {
  variant: TextVariant;
  style: TextStyle;
}
const INITIAL_DEFAULTS: TextDefaults = { variant: 'plain', style: 'red-outline' };

export function AnnotationCanvas({
  ticketId,
  screenshotUrl,
  initialShapes,
  carouselRightSlot,
  stageMedia,
  attachments,
  initialAnnotationVersion,
  clickMarks,
}: AnnotationCanvasProps) {
  const currentTicketRef = useRef(ticketId);
  currentTicketRef.current = ticketId;
  const canvasMountedRef = useRef(false);
  useEffect(() => {
    canvasMountedRef.current = true;
    return () => {
      canvasMountedRef.current = false;
    };
  }, []);
  const qc = useQueryClient();
  const toast = useToast();
  const toastRef = useRef(toast);
  useEffect(() => {
    toastRef.current = toast;
  }, [toast]);
  const { user } = useAuth();
  const pendingWriterIdRef = useRef<string | null>(null);
  const pendingWriterId = pendingWriterIdRef.current ?? globalThis.crypto.randomUUID();
  pendingWriterIdRef.current = pendingWriterId;
  const [tool, setTool] = useState<AnnotTool>('pen');
  /** Bumped when the bug sprite decodes, so the canvas draws it. */
  const [spriteGeneration, setSpriteGeneration] = useState(0);
  const [color, setColor] = useState<HighlightColor>('red');
  const [zoom, setZoom] = useState(1);
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [redoStack, setRedoStack] = useState<Shape[]>([]);
  const [activeShape, setActiveShape] = useState<Shape | null>(null);
  // The shape stack the server most recently confirmed. Used by the
  // collaborative-merge logic to identify which local shapes are
  // unsynced additions vs which are baseline so a teammate's incoming
  // snapshot can be merged on top without clobbering pending edits.
  // Updated on (a) hydration from initialShapes, (b) each successful
  // POST /annotations save, (c) each incoming annotation.updated event
  // we apply.
  const syncedBaseRef = useRef<Shape[]>([]);
  // The shapes on screen, for code that runs outside a render: effect
  // cleanups, and the live-event subscription, which re-subscribing on
  // every stroke would otherwise need. Assigned on every render, and also
  // set by hand wherever the base above is replaced together with a
  // `setShapes`, so the two agree while that render is pending.
  //
  // They must: the flush-on-leave cleanup compares them, and a cleanup can
  // run in that gap. React's development double-mount runs one straight
  // after the load, when the working copy was still the empty list it starts
  // as, and the flush took the difference for the person deleting every
  // shape and saved an empty list over them (29 Sep manual testing).
  const shapesRef = useRef<Shape[]>(shapes);
  shapesRef.current = shapes;
  // The working copy as this visit loaded it, before any edit here: the
  // flush-on-leave cleanup saves edits, not the load.
  const loadedShapesRef = useRef<Shape[] | null>(null);
  const warnAnnotationNotSynced = (forTicket: string, description: string) => {
    if (annotationWarningRegistry.has(forTicket)) return;
    let toastId = '';
    toastId = toastRef.current.push({
      variant: 'warning',
      title: 'Annotation not synced',
      description,
      onDismiss: () => {
        annotationWarningRegistry.release(forTicket, toastId);
      },
    });
    annotationWarningRegistry.register(forTicket, toastId);
  };
  const clearAnnotationWarning = (forTicket: string) => {
    const warningId = annotationWarningRegistry.take(forTicket);
    if (!warningId) return;
    toastRef.current.dismiss(warningId);
    toastRef.current.push({
      variant: 'success',
      title: 'Annotations synced',
      description: 'Your saved drawing is available to teammates again.',
    });
  };

  // Text modal state. The modal is now only used to edit *style* of an
  // existing shape (styleOnly) or to seed/edit defaults (defaultsMode).
  // New text boxes are created inline without a modal — drag creates the
  // rect and the inline textarea inside the selection overlay takes focus.
  const [textModalTarget, setTextModalTarget] = useState<null | (Shape & { kind: 'textBox' })>(
    null,
  );
  const [textModalIsDefaults, setTextModalIsDefaults] = useState(false);
  const [textModalStyleOnly, setTextModalStyleOnly] = useState(false);

  // Selected text box
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const pendingPinFocusRef = useRef<string | null>(null);
  const pendingToolbarFocusRef = useRef(false);
  const [pinTabStopId, setPinTabStopId] = useState<string | null>(null);
  const [pinAnnouncement, setPinAnnouncement] = useState<{ sequence: number; text: string } | null>(
    null,
  );
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  // Image id pending removal confirmation. Set when the user clicks ×
  // on a thumbnail that has shapes on it (empty thumbnails skip the
  // dialog and remove immediately). Null when no confirm is open.
  const [removeImageTarget, setRemoveImageTarget] = useState<string | null>(null);

  // Dragging / resizing a selected rectangle-shaped annotation (textBox or redact).
  const dragModeRef = useRef<AnnotationDrag | null>(null);
  const setDragMode = (next: AnnotationDrag | null) => {
    dragModeRef.current = next;
  };

  // Panning state
  const [panState, setPanState] = useState<null | {
    startClientX: number;
    startClientY: number;
    startScrollLeft: number;
    startScrollTop: number;
  }>(null);

  // Hold space → temporary pan, like Figma. While true, click+drag pans
  // regardless of the active tool. Released-space restores the prior
  // tool. Skipped when the user is typing in an input/textarea so the
  // canvas-level handler doesn't swallow space in text annotations.
  const [spaceHeld, setSpaceHeld] = useState(false);

  // Images (original + uploaded). The "original" Capture slot only exists
  // when the ticket actually has a screenshot — text-only tickets should
  // start with an empty carousel so the first upload sits at index 0, not
  // awkwardly behind an empty placeholder. Loading + persistence of the
  // uploaded extras happens in the effects below.
  const [images, setImages] = useState<StageImage[]>([]);
  const [activeImageId, setActiveImageId] = useState<string>('');
  const rasterContextsRef = useRef(new AnnotationRasterContextRegistry<AnnotationRasterContext>());
  useLayoutEffect(() => {
    rasterContextsRef.current.set(ticketId, { images, screenshotUrl });
  }, [ticketId, images, screenshotUrl]);
  // Two-phase guard for the load → save effect pair.
  //
  //   hydratedTicketRef: which ticket's data is currently in state. Used
  //     so the save-effect on a ticket switch doesn't persist the prior
  //     ticket's images under the new ticket's id.
  //
  //   pendingHydrationRef: true after the load-effect fires but before
  //     React has re-rendered with the hydrated images. The save-effect
  //     flips this to false and skips persisting — otherwise it races
  //     against the pending setImages and writes an empty array over the
  //     real data. Also covers StrictMode's intentional double-invocation
  //     on dev mount (each mount pass re-primes the guard).
  //
  //   shapesPendingHydrationRef: same guard for the shapes (drawings)
  //     persistence. Drawings had the identical bug — navigate away, come
  //     back, setShapes(stored) races setShapes-drop-to-[] from the save
  //     effect's view of stale state and your drawings disappeared.
  const hydratedTicketRef = useRef<string | null>(null);
  const pendingHydrationRef = useRef<boolean>(false);
  // ---- server-attachment bookkeeping (TOADO-261) ----
  // attachmentsRef: latest prop, readable from the [ticketId]-scoped
  //   hydration effect without widening its deps.
  // serverImageMapRef: carousel imageId -> attachment row id, for
  //   delete calls and for the "is this image persisted?" checks that
  //   gate the localStorage fallback buffer and the uploader.
  // serverSeenRef: imageIds the attachments PROP has confirmed. Only
  //   these may be treated as teammate-deleted when they disappear
  //   from the prop - an id we confirmed via our own upload stays put
  //   until the next refetch includes it.
  // uploadAttemptedRef: stops a failing upload from hot-looping within
  //   a mount (failed uploads stay in the localStorage buffer and
  //   retry next mount). The in-flight and removed-while-uploading
  //   sets live at module scope - see their declaration.
  const attachmentsRef = useRef<Attachment[]>(attachments ?? []);
  attachmentsRef.current = attachments ?? [];
  const serverImageMapRef = useRef<Map<string, string>>(new Map());
  const serverSeenRef = useRef<Set<string>>(new Set());
  const uploadAttemptedRef = useRef<Set<string>>(new Set());
  const shapesPendingHydrationRef = useRef<boolean>(false);
  // Set to true when a remote `annotation.updated` event has just been
  // applied OR after the initial hydration. The upload effect checks +
  // clears it on the next run so we don't echo the merged snapshot
  // back to the server (which would bounce the same event to every
  // other client and create a feedback loop) and we don't re-POST the
  // freshly-loaded snapshot back to the server on every page open.
  // Distinct from `shapesPendingHydrationRef` because the localStorage
  // save effect resets that one — without a separate guard the upload
  // effect would see the flag flipped to false and fire its debounce.
  const skipNextUploadRef = useRef<boolean>(false);
  // Ticket id we've already hydrated shapes for. After the first
  // hydration, `initialShapes` prop changes are ignored: the live-event
  // handler is the source of truth for shape updates, and any refetch
  // (triggered by an unrelated event like `comment.created`) would
  // otherwise clobber the user's in-progress strokes by passing a new
  // (same-content) prop reference into the hydration effect.
  const hydratedForTicketRef = useRef<string | null>(null);
  const incorporatedPendingRef = useRef<Map<string, PendingAnnotationRecord[]>>(new Map());

  // Text defaults
  const [textDefaults, setTextDefaults] = useState<TextDefaults>(() => {
    try {
      const raw = localStorage.getItem(DEFAULT_TEXT_STYLE_KEY);
      return raw ? JSON.parse(raw) : INITIAL_DEFAULTS;
    } catch {
      return INITIAL_DEFAULTS;
    }
  });
  useEffect(() => {
    localStorage.setItem(DEFAULT_TEXT_STYLE_KEY, JSON.stringify(textDefaults));
  }, [textDefaults]);

  // Per-image projections of the global shape + redo stacks. Rendering,
  // hit-testing, and undo/redo/clear all operate on the active image's
  // slice so annotations stay scoped to where they were drawn.
  // Memoized so the draw effect, blur overlay, and selection derivations
  // share a single reference per render rather than re-filtering each.
  const activeImageShapes = useMemo(
    () => shapes.filter((s) => s.imageId === activeImageId),
    [shapes, activeImageId],
  );
  const activeImageRedoStack = useMemo(
    () => redoStack.filter((s) => s.imageId === activeImageId),
    [redoStack, activeImageId],
  );
  // In-progress drag, but only when tagged with the current active
  // image. selectImage / removeImage clear `activeShape` on switch
  // so this is normally identical to `activeShape`; the imageId check
  // defends against any path that mutates activeImageId WITHOUT
  // clearing activeShape (e.g. the auto-advance effect that runs when
  // the active slot vanishes server-side). Without it, a stranded
  // mid-drag could bleed onto the next image's canvas.
  const visibleActiveShape =
    activeShape && activeShape.imageId === activeImageId ? activeShape : null;

  const scrollRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  /**
   * Replace the server's base and the working copy together. `shapesRef` is
   * set here as well as by the render that follows; see its comment for the
   * cleanup that runs in between. A function for `working` is applied to the
   * ref now and handed to `setShapes` as well, so an edit already queued
   * for the next render is kept.
   */
  const replaceShapes = (base: Shape[], working: Shape[] | ((current: Shape[]) => Shape[])): void => {
    syncedBaseRef.current = base;
    shapesRef.current = typeof working === 'function' ? working(shapesRef.current) : working;
    setShapes(working);
  };

  /** This person's own saves on a ticket, in this tab: see `annotationOwnSaves`. */
  const ownSavesFor = (forTicket: string): string | null => {
    const owner = user ? captureAuthSession(user.id) : null;
    return owner ? ownSavesKey(owner, forTicket) : null;
  };

  /**
   * A copy of the server's shapes, from a live event or from a fresher copy
   * of the ticket than the one this canvas loaded from, becomes the base with
   * what was done here since kept on top: since this person's own save, if it
   * is one, and since the base otherwise (`receiveServerAnnotations`).
   */
  const receiveServerShapes = (forTicket: string, remote: Shape[], version: number | undefined): void => {
    const key = ownSavesFor(forTicket);
    const ownBasis = key === null ? null : ownSaveBasis(key, remote, version);
    if (key !== null && version !== undefined) observeServerVersion(key, version, remote);
    const next = receiveServerAnnotations(remote, shapesRef.current, syncedBaseRef.current, ownBasis);
    // With no local work left over, re-posting would bounce the same
    // snapshot to every other client (echo loop). With some, the normal
    // upload debounce saves it.
    if (!next.republish) skipNextUploadRef.current = true;
    replaceShapes(next.base, next.working);
  };

  /** Tell the cached ticket about a newer annotation row: see `withAnnotationRow`. */
  const rememberServerShapes = (forTicket: string, snapshot: Shape[], version: number): void => {
    qc.setQueryData<Ticket>(['ticket', forTicket], (ticket) =>
      ticket && withAnnotationRow(ticket, forTicket, snapshot, version, user?.id ?? '', new Date().toISOString()));
  };

  // Persist shapes per ticket. Guarded with the same pending-hydration
  // pattern as images so the save-effect doesn't fire with stale shapes
  // between setShapes() and the re-render that consumes it.
  //
  // Hydration order:
  //   1. Server snapshot (`initialShapes`, sourced from the Ticket
  //      payload's annotations row). Authoritative for collaborative
  //      editing — every team member opening the ticket sees the same
  //      shapes here.
  //   2. localStorage fallback. Catches two cases: (a) pre-existing
  //      annotations from before server-sync shipped — those still
  //      live only in the original author's browser until the next
  //      edit triggers a server save, and (b) offline edits the user
  //      made while disconnected.
  //   3. Empty canvas. New ticket, no shapes anywhere.
  //
  // The save-effect below writes to BOTH localStorage and the server
  // on every change so the two stay in sync going forward.
  useEffect(() => {
    // Protected routes normally do not mount the canvas before auth resolves.
    // If they ever do, defer hydration so the user-scoped pending record is
    // not skipped permanently by the once-per-ticket guard.
    if (!user) return;
    // Only hydrate once per ticket. After the first run, later
    // `initialShapes` prop changes (which arrive on every ticket
    // refetch — comment posted, ticket assigned, AI title written,
    // etc.) are merged, not loaded, and only when they carry a newer
    // version. Re-running the body would call `setShapes(loaded)` and
    // wipe any strokes the user is mid-drawing.
    if (hydratedForTicketRef.current === ticketId) {
      // A fresher copy of the ticket than the one this canvas loaded from.
      // The cached copy it opened with can predate a teammate's edit made
      // while the ticket was closed, and the fresh one that follows it was
      // ignored until the next edit here. Taken like a teammate's live edit,
      // rebased under anything drawn since, and only when newer than every
      // version this canvas has seen, so the refetch a comment or a title
      // causes, which carries the same row, changes nothing.
      if (
        initialAnnotationVersion !== undefined &&
        initialAnnotationVersion > lastAnnotationVersionRef.current
      ) {
        lastAnnotationVersionRef.current = initialAnnotationVersion;
        receiveServerShapes(
          ticketId,
          Array.isArray(initialShapes)
            ? (initialShapes as Shape[]).map((shape) => normalizeShape(shape))
            : [],
          initialAnnotationVersion,
        );
      }
      return;
    }
    hydratedForTicketRef.current = ticketId;
    // Seed the event-ordering guard from the hydrated row's version so
    // an event queued from BEFORE this payload cannot regress it.
    lastAnnotationVersionRef.current = initialAnnotationVersion ?? 0;

    let loaded: Shape[] = [];
    let serverConfirmed: Shape[] = [];
    if (Array.isArray(initialShapes) && initialShapes.length > 0) {
      serverConfirmed = (initialShapes as Shape[]).map((shape) => normalizeShape(shape));
    }
    const pendingRaw = readPendingAnnotations(localStorage, ticketId, user.id);
    incorporatedPendingRef.current.set(ticketId, pendingRaw);
    const pending = pendingRaw.map((snapshot) => ({
      ...snapshot,
      base: snapshot.base.map((shape) => normalizeShape(shape)),
      current: snapshot.current.map((shape) => normalizeShape(shape)),
    }));
    if (pending.length > 0) {
      loaded = recoverPendingAnnotations(serverConfirmed, pending);
    } else if (serverConfirmed.length > 0) {
      loaded = serverConfirmed;
    } else {
      const raw = localStorage.getItem(`vd-annot-${ticketId}`);
      if (raw) {
        try {
          loaded = (JSON.parse(raw) as Shape[]).map((shape) => normalizeShape(shape));
        } catch {
          /* noop */
        }
      }
      // localStorage shapes haven't been confirmed by the server (might
      // be offline edits). Treat them as local additions on top of an
      // empty base so a remote update merges correctly.
      serverConfirmed = [];
    }
    // Skip the localStorage save AND the server upload that would
    // otherwise fire on the resulting setShapes() — neither
    // represents a user edit, just the load itself.
    shapesPendingHydrationRef.current = true;
    skipNextUploadRef.current = true;
    replaceShapes(serverConfirmed, loaded);
    loadedShapesRef.current = loaded;
    setRedoStack([]);
    setActiveShape(null);
    setSelectedId(null);
    setPinAnnouncement(null);
  }, [ticketId, initialShapes, initialAnnotationVersion, user]);
  useEffect(() => {
    if (shapesPendingHydrationRef.current) {
      // First save after load primed the guard; `shapes` here is still
      // the stale pre-hydration value. Skip this write so we don't
      // clobber the ticket's saved drawings.
      shapesPendingHydrationRef.current = false;
      return;
    }
    try {
      localStorage.setItem(`vd-annot-${ticketId}`, JSON.stringify(shapes));
      if (!user) return;
      const pendingResult = persistPendingAnnotations(
        localStorage,
        ticketId,
        user.id,
        pendingWriterId,
        syncedBaseRef.current,
        shapes,
      );
      if (pendingResult === 'failed') throw new Error('pending annotation storage failed');
      pendingPersistFailures.delete(ticketId);
    } catch {
      pendingPersistFailures.add(ticketId);
      warnAnnotationNotSynced(
        ticketId,
        'This browser could not back up the pending change. Keep this page open and make another edit to retry the server save.',
      );
    }
  }, [shapes, ticketId, user]);

  // Subscribe to annotation edits. The server emits `annotation.updated`
  // for every successful save and fans it out to all clients in the same
  // company, the saver included; `remoteAnnotationUpdate` decides which
  // this canvas takes, and why its own are not filtered out.
  //
  // We read `shapes` via a ref (`shapesRef`) instead of putting it in
  // the effect deps because re-running the subscription on every
  // shape change would tear down + re-create the api.subscribe
  // callback ~once per stroke, which is wasteful and creates a window
  // where remote events are dropped.
  //
  // The newest row version this canvas has seen, from its load, its own
  // saves and these events; an event no newer is not taken (review round 7).
  const lastAnnotationVersionRef = useRef<number>(0);
  useEffect(() => {
    return api.subscribe((e: LiveEvent) => {
      const update = remoteAnnotationUpdate(e, ticketId, lastAnnotationVersionRef.current);
      if (!update) return;
      if (update.version !== undefined) {
        lastAnnotationVersionRef.current = update.version;
        rememberServerShapes(ticketId, update.remote, update.version);
      }
      receiveServerShapes(ticketId, update.remote, update.version);
    });
  }, [ticketId, user?.id]);

  // --------------- Rasterize image + shapes, upload to server ---------------
  //
  // Shapes JSON is the canonical, editable source of truth. But cards,
  // board thumbnails, and MCP tools all want a flat "what does this look
  // like?" image that includes the drawings on top. We composite them
  // here (debounced so a burst of pen strokes is one upload, not ten)
  // and POST the PNG data URL to the server. The server stores it and
  // updates `ticket.capture.screenshotAnnotatedUrl`, which the ticket
  // serializer resolves through the storage adapter.
  const uploadTimerRef = useRef<number | null>(null);
  useEffect(() => {
    // `skipNextUploadRef` covers the live-event merge case: when a
    // remote update was applied with no pending local changes, the
    // merged shapes equal the remote shapes and re-posting them would
    // bounce the same snapshot into a feedback loop.
    if (skipNextUploadRef.current) {
      skipNextUploadRef.current = false;
      return;
    }
    // Reference-equality check covers the post-hydration / post-save
    // / post-remote-merge cases: in all of them we set shapes to the
    // SAME array reference we stored in syncedBaseRef. A user edit
    // always produces a fresh array via setShapes(prev => [...prev,
    // newShape]), so this only short-circuits the no-op POSTs that
    // would otherwise fire on every mount.
    //
    // Without this guard, the upload effect runs once with shapes=[]
    // (consuming any skip flag), then again with shapes=loaded after
    // the hydration's setShapes commits — and that second run
    // schedules a 500ms POST of the just-loaded shapes. If the user
    // draws + navigates + comes back faster than the round trip
    // takes to land, that no-op POST overwrites their freshly-flushed
    // stroke on the server.
    // Known, accepted wobble (review round 10): after a save whose
    // snapshot the server FILTERED (shapes on a not-yet-uploaded
    // image), the base holds the stored array while state holds the
    // sent reference, so this identity check misses until the
    // post-upload nudge restores it - dep changes in that bounded
    // window schedule redundant saves of unchanged content. Left as
    // is deliberately: an id-set comparison here would skip REAL
    // edits (moving a shape changes content, not ids), which is a
    // data-loss class of bug, not an efficiency one.
    if (shapes === syncedBaseRef.current) return;

    // Debounce: coalesce rapid-fire shape changes (pen strokes, drag
    // resizes, undo/redo bursts) into a single upload ~500ms after the
    // last edit.
    if (uploadTimerRef.current) window.clearTimeout(uploadTimerRef.current);
    uploadTimerRef.current = window.setTimeout(() => {
      uploadTimerRef.current = null;
      // Save the editable JSON before uploading its derived raster. If the
      // canonical write fails, publishing newer card/MCP pixels would put
      // the flattened view ahead of the recoverable source of truth.
      // Snapshot the shapes we're about to send so we can advance
      // syncedBaseRef on success. Capturing here (rather than in the
      // .then) protects against a race where `shapes` changes again
      // mid-flight — the syncedBase has to reflect what THIS request
      // actually persisted, not whatever happens to be in state when
      // the response lands. We persist the FULL multi-image array so
      // every image's shapes round-trip through the server in one row.
      const sentShapes = shapes;
      const saveTicketId = ticketId;
      const rasterImages = images;
      const rasterScreenshotUrl = screenshotUrl;
      const saveBase = syncedBaseRef.current;
      const expectedVersion = lastAnnotationVersionRef.current;
      const owner = user ? captureAuthSession(user.id) : null;
      if (!owner) return;
      // This save among this person's own, so the canvas that hears it come
      // back, this one or the next, knows it: see `annotationOwnSaves`.
      const ownKey = ownSavesKey(owner, saveTicketId);
      const ownSave = {};
      void enqueueAnnotationPersistence({
        ticketId: saveTicketId,
        owner,
        // Kept among this person's saves: a timeout does not say the server
        // did not store it.
        onTimeout: () =>
          warnAnnotationNotSynced(
            saveTicketId,
            'This save took too long. Your change remains backed up in this browser and the next edit will retry it.',
          ),
        operation: async (signal) => {
          let saved: Awaited<ReturnType<typeof saveAnnotationSnapshot>>;
          try {
            saved = await saveAnnotationSnapshot({
              snapshot: sentShapes,
              base: saveBase,
              expectedVersion,
              signal,
              save: (candidate, version, requestSignal) => {
                recordSent(ownKey, ownSave, sentShapes, candidate, version);
                return api.saveAnnotations(saveTicketId, candidate, {
                  expectedVersion: version,
                  signal: requestSignal,
                });
              },
              ownBasis: (snapshot, version) => ownSaveBasis(ownKey, snapshot, version),
            });
          } catch (err) {
            if (answeredWithError(err)) forgetSent(ownKey, ownSave);
            if (signal.aborted) return;
            console.warn('[annotations] shape save failed', err);
            const backedUp = !pendingPersistFailures.has(saveTicketId);
            warnAnnotationNotSynced(
              saveTicketId,
              backedUp
                ? 'Your change is backed up in this browser but has not reached the server. It will retry after reload or another edit.'
                : 'Your change has not reached the server and this browser could not back it up. Keep this page open and make another edit to retry.',
            );
            return;
          }
          // Base = what the server ACTUALLY stored (review round 8). The
          // save endpoint withholds shapes for images that have no
          // attachment row yet; recording `sentShapes` as confirmed made
          // the three-way merge treat those withheld shapes as baseline,
          // so a teammate's event deleted them from local state
          // permanently. When nothing was filtered, keep the sentShapes
          // reference - identity with current state is what lets the
          // upload effect skip no-op re-posts on unrelated dep changes.
          // Version-gated (review round 9): two saves can be in flight
          // and their responses can arrive out of order. Adopting an
          // OLDER response's snapshot as the merge base after a newer
          // save or event already established a later one would rebase
          // future teammate events against a stale stack. Equality is
          // allowed - our own write's event may have advanced the guard
          // before this response landed. Versionless responses (older
          // api builds) keep the pre-round-8 behavior.
          const resVersion = saved.version;
          const persistedShapes = saved.sent;
          const confirmed = saved.confirmed;
          const isCurrentTicket =
            canvasMountedRef.current && currentTicketRef.current === saveTicketId;
          const supersededByLiveEvent =
            isCurrentTicket &&
            resVersion !== undefined &&
            resVersion < lastAnnotationVersionRef.current;
          if (isCurrentTicket && !supersededByLiveEvent) {
            if (resVersion !== undefined && resVersion > lastAnnotationVersionRef.current) {
              lastAnnotationVersionRef.current = resVersion;
            }
            if (snapshotValueEqual(persistedShapes, sentShapes)) {
              // What was sent is what was stored: the working copy already
              // holds it, and anything since, so only the base moves.
              syncedBaseRef.current = confirmed;
            } else {
              // The save was rebased over a conflict: the working copy takes
              // the other side's changes too, in step with the base, and is
              // the stored array itself when nothing was done here since
              // (see `mergeRemoteAnnotations`).
              replaceShapes(confirmed, (current) => {
                const rebased = rebaseShapes(persistedShapes, current, sentShapes);
                return snapshotValueEqual(rebased, confirmed) ? confirmed : rebased;
              });
            }
          }
          // Known from here by the version it was stored as, which is how its
          // announcement and any refetched copy of the ticket will name it.
          recordAnswered(ownKey, ownSave, resVersion);
          observeServerVersion(ownKey, resVersion, persistedShapes);
          rememberServerShapes(saveTicketId, confirmed, resVersion);
          try {
            const pendingResult = acknowledgePendingAnnotations(
              localStorage,
              saveTicketId,
              owner.userId,
              pendingWriterId,
              persistedShapes,
              supersededByLiveEvent ? syncedBaseRef.current : confirmed,
              isCurrentTicket ? shapesRef.current : undefined,
            );
            if (pendingResult === 'failed') throw new Error('pending annotation storage failed');
            const incorporated = incorporatedPendingRef.current.get(saveTicketId) ?? [];
            const retireResult = retireIncorporatedPendingAnnotations(localStorage, incorporated);
            if (retireResult === 'failed') throw new Error('pending annotation retirement failed');
            incorporatedPendingRef.current.delete(saveTicketId);
            pendingPersistFailures.delete(saveTicketId);
            if (pendingResult === 'cleared') clearAnnotationWarning(saveTicketId);
          } catch {
            pendingPersistFailures.add(saveTicketId);
            warnAnnotationNotSynced(
              saveTicketId,
              'The server accepted this edit, but the browser could not back up a newer pending change. Keep this page open and edit again to retry.',
            );
          }
          if (supersededByLiveEvent) return;

          try {
            // The first image with its own shapes, whichever is on screen:
            // the PNG is the card thumbnail and the annotated capture
            // (TOADO-716), never another image or a mash-up across images.
            const dataUrl = await rasterizeAnnotatedImage(
              rasterSource(rasterImages, persistedShapes, rasterScreenshotUrl),
            );
            if (!dataUrl) {
              return; // nothing to raster (no image + no shapes)
            }
            await api.saveAnnotationRaster(saveTicketId, dataUrl, {
              annotationVersion: resVersion,
              signal,
            });
            // Refresh card lists + the inbox so the new annotated thumbnail
            // shows up elsewhere without a full reload. Deliberately skip
            // ['ticket', ticketId] invalidation: the local user already
            // has the latest shapes (they just drew them), and re-fetching
            // the ticket payload pulls a freshly-presigned screenshot URL
            // every time (X-Amz-Date / signature change per presign call),
            // which causes the carousel image element to flash on every
            // edit. The next page load gets fresh server data anyway.
            qc.invalidateQueries({ queryKey: ['tickets'] });
            qc.invalidateQueries({ queryKey: ['inbox'] });
          } catch (err) {
            // Network blips, quota errors, or the image failing to load
            // shouldn't break the editor. Log and keep going. The shapes
            // themselves are safe in localStorage and on next edit we'll
            // retry the raster.
            if (!signal.aborted && !isSupersededRasterError(err)) {
              console.warn('[annotations] raster upload failed', err);
            }
          }
        },
      });
    }, 500);
    return () => {
      if (uploadTimerRef.current) {
        window.clearTimeout(uploadTimerRef.current);
        uploadTimerRef.current = null;
      }
    };
  }, [shapes, ticketId, images, screenshotUrl]);

  // Flush a pending save when the user leaves the ticket — either by
  // navigating to a different ticket (ticketId changes) or by leaving
  // the ticket detail page entirely (component unmounts). Without
  // this, drawing a stroke and immediately leaving (within the 500ms
  // debounce window) drops the stroke: the upload effect's cleanup
  // cancels the timer, no POST goes out, and the next time the user
  // opens the ticket they hydrate from the pre-stroke server snapshot.
  //
  // The flush is based on snapshot content, not timer state. Effect cleanup
  // ordering is not a persistence contract: the upload effect may clear its
  // timer before this cleanup runs. Comparing the working copy with the last
  // confirmed base keeps immediate navigation safe in either order.
  //
  // Latest shapes are read via `shapesRef` because this effect's deps
  // are `[ticketId]` only — the closure captures whatever shapes was
  // at the last ticketId change, which is stale by the time the user
  // is mid-drawing.
  useEffect(() => {
    return () => {
      const sentShapes = shapesRef.current;
      // Edits made on this visit, not the load itself. Shapes recovered from
      // the browser's backup are retried by the upload effect, as its warnings
      // promise, and leaving without an edit does not flush them, as it never
      // did on main, where this flush ran only for a save still pending.
      // Without this, React's development double-mount, which runs this
      // cleanup straight after the load, saved them the moment a ticket
      // opened, and moved the version under the first edit's save.
      const hasUnconfirmedChanges = sentShapes !== loadedShapesRef.current
        && !snapshotValueEqual(sentShapes, syncedBaseRef.current);
      if (uploadTimerRef.current) {
        window.clearTimeout(uploadTimerRef.current);
        uploadTimerRef.current = null;
      }
      if (hasUnconfirmedChanges) {
        const rasterContext = rasterContextsRef.current.get(ticketId);
        const saveBase = syncedBaseRef.current;
        const expectedVersion = lastAnnotationVersionRef.current;
        const owner = user ? captureAuthSession(user.id) : null;
        if (!owner) return;
        // Among this person's own saves, for the canvas that opens this
        // ticket next and hears it come back: see `annotationOwnSaves`.
        const ownKey = ownSavesKey(owner, ticketId);
        const ownSave = {};
        // Fire and forget because the component is leaving. The shared queue
        // keeps this complete JSON plus raster revision behind earlier work.
        void enqueueAnnotationPersistence({
          ticketId,
          owner,
          // Kept among this person's saves: a timeout does not say the server
          // did not store it.
          onTimeout: () =>
            warnAnnotationNotSynced(
              ticketId,
              'This save took too long. Your change remains backed up in this browser and will retry when you reopen the ticket.',
            ),
          operation: async (signal) => {
            let saved: Awaited<ReturnType<typeof saveAnnotationSnapshot>>;
            try {
              saved = await saveAnnotationSnapshot({
                snapshot: sentShapes,
                base: saveBase,
                expectedVersion,
                signal,
                save: (candidate, version, requestSignal) => {
                  recordSent(ownKey, ownSave, sentShapes, candidate, version);
                  return api.saveAnnotations(ticketId, candidate, {
                    expectedVersion: version,
                    signal: requestSignal,
                  });
                },
                ownBasis: (snapshot, version) => ownSaveBasis(ownKey, snapshot, version),
              });
            } catch (err) {
              if (answeredWithError(err)) forgetSent(ownKey, ownSave);
              if (signal.aborted) return;
              console.warn('[annotations] flush-on-leave save failed', err);
              const backedUp = !pendingPersistFailures.has(ticketId);
              warnAnnotationNotSynced(
                ticketId,
                backedUp
                  ? 'Your change is backed up in this browser but did not reach the server before navigation. It will retry when you reopen the ticket.'
                  : 'Your change did not reach the server and this browser could not back it up. Return to the ticket before closing this page.',
              );
              return;
            }
            const persistedShapes = saved.sent;
            const confirmed = saved.confirmed;
            recordAnswered(ownKey, ownSave, saved.version);
            observeServerVersion(ownKey, saved.version, persistedShapes);
            rememberServerShapes(ticketId, confirmed, saved.version);
            try {
              const pendingResult = acknowledgePendingAnnotations(
                localStorage,
                ticketId,
                owner.userId,
                pendingWriterId,
                persistedShapes,
                confirmed,
              );
              if (pendingResult === 'failed') throw new Error('pending annotation storage failed');
              const incorporated = incorporatedPendingRef.current.get(ticketId) ?? [];
              const retireResult = retireIncorporatedPendingAnnotations(localStorage, incorporated);
              if (retireResult === 'failed') throw new Error('pending annotation retirement failed');
              incorporatedPendingRef.current.delete(ticketId);
              pendingPersistFailures.delete(ticketId);
              if (pendingResult === 'cleared') clearAnnotationWarning(ticketId);
            } catch {
              pendingPersistFailures.add(ticketId);
              warnAnnotationNotSynced(
                ticketId,
                'The server accepted this edit, but the browser could not retain a newer pending change. Reopen the ticket before editing further.',
              );
            }
            if (!rasterContext) return;
            try {
              const dataUrl = await rasterizeAnnotatedImage(
                rasterSource(rasterContext.images, persistedShapes, rasterContext.screenshotUrl),
              );
              if (!dataUrl) return;
              await api.saveAnnotationRaster(ticketId, dataUrl, {
                annotationVersion: saved.version,
                signal,
              });
              qc.invalidateQueries({ queryKey: ['tickets'] });
              qc.invalidateQueries({ queryKey: ['inbox'] });
            } catch (err) {
              if (!signal.aborted && !isSupersededRasterError(err))
                console.warn('[annotations] flush-on-leave raster upload failed', err);
            }
          },
        }).finally(() => {
          rasterContextsRef.current.delete(ticketId, rasterContext);
        });
      } else {
        rasterContextsRef.current.delete(ticketId);
      }
    };
  }, [ticketId, user?.id]);

  // Load uploaded images from localStorage when the TICKET changes. The
  // "original" Capture slot is re-derived from screenshotUrl; only
  // extras (user uploads + screen captures) persist.
  //
  // Deliberately scoped to `[ticketId]` — NOT `[ticketId, screenshotUrl]`.
  // The screenshot URL is presigned and re-rolls on every ticket refetch
  // (different X-Amz-Signature each call). Any live event for this
  // ticket invalidates the ticket query upstream, so screenshotUrl flips
  // on every comment, every AI-title arrival, every annotation echo.
  // Including it here used to slam activeImageId back to the original
  // every time the user was looking at an uploaded image. The companion
  // effect below patches the original's URL in place without touching
  // active selection.
  useEffect(() => {
    // Server truth first (TOADO-261): persisted attachments seed the
    // carousel. localStorage remains only the buffer for images that
    // have not reached the server yet (fresh adds mid-upload, offline
    // edits, and pre-migration legacy extras) - anything the server
    // already has is filtered out of it so nothing doubles up.
    const server = attachmentsRef.current;
    serverImageMapRef.current = new Map(server.map((a) => [a.imageId, a.id]));
    serverSeenRef.current = new Set(server.map((a) => a.imageId));
    // uploadAttemptedRef is per-mount-per-ticket (a fresh ticket open
    // retries failed uploads). The in-flight and removed-while-
    // uploading sets are module-level and never reset here - they
    // survive ticket switches AND unmount/remount (review rounds 5+8).
    uploadAttemptedRef.current = new Set();
    const serverImages: StageImage[] = server.map((a) => ({
      id: a.imageId,
      label: a.label || 'Image',
      url: a.imageUrl,
    }));
    let extras: StageImage[] = [];
    const raw = localStorage.getItem(`vd-annot-imgs-${ticketId}`);
    if (raw) {
      try {
        extras = (JSON.parse(raw) as StageImage[]).filter(
          (i) => !serverImageMapRef.current.has(i.id),
        );
      } catch {
        /* noop */
      }
    }
    const nextOriginal: StageImage | null = screenshotUrl
      ? { id: ORIGINAL_IMAGE_ID, label: 'Capture', url: screenshotUrl }
      : null;
    const next = [...(nextOriginal ? [nextOriginal] : []), ...serverImages, ...extras];
    // Prime the save-effect guard BEFORE queueing the state update. When
    // the save-effect fires in this same commit phase (with stale images
    // that still reflects the prior ticket / initial []), it sees
    // pendingHydration=true, flips it to false, and skips the write.
    pendingHydrationRef.current = true;
    hydratedTicketRef.current = ticketId;
    setImages(next);
    setActiveImageId(next[0]?.id ?? '');
    // screenshotUrl is intentionally omitted from deps — see comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketId]);

  // Patch the "original" image's URL in place when the presigned
  // screenshotUrl rotates (same ticket, fresh signature). Leaves
  // activeImageId untouched so the carousel doesn't jump back to the
  // capture every time a live event triggers a ticket refetch.
  useEffect(() => {
    setImages((prev) => {
      const idx = prev.findIndex((i) => i.id === ORIGINAL_IMAGE_ID);
      if (!screenshotUrl) {
        // Capture was cleared on the server (rare; possible if a future
        // admin path deletes the screenshot). Drop the slot. The
        // companion effect below advances activeImageId off it if the
        // user was viewing it.
        return idx < 0 ? prev : prev.filter((i) => i.id !== ORIGINAL_IMAGE_ID);
      }
      if (idx >= 0 && prev[idx]?.url === screenshotUrl) return prev;
      const orig: StageImage = { id: ORIGINAL_IMAGE_ID, label: 'Capture', url: screenshotUrl };
      if (idx < 0) return [orig, ...prev];
      const next = [...prev];
      next[idx] = orig;
      return next;
    });
  }, [screenshotUrl]);

  // Reconcile teammate attachment changes on refetch (TOADO-261).
  // Keyed on a stable signature, NOT the array reference - the ticket
  // query refetches on every live event and each refetch carries fresh
  // presigned URLs, so reference- or URL-sensitive deps would churn
  // state (and flash <img> elements) constantly. Additions append with
  // the server URL; removals only apply to ids the PROP itself
  // previously confirmed (serverSeenRef), so an image we just uploaded
  // ourselves cannot be yanked by a prop that predates it.
  const attachmentsSig = (attachments ?? [])
    .map((a) => `${a.imageId}:${a.id}`)
    .sort()
    .join('|');
  useEffect(() => {
    if (hydratedTicketRef.current !== ticketId) return;
    const server = attachmentsRef.current;
    const serverIds = new Set(server.map((a) => a.imageId));
    const removedIds = [...serverSeenRef.current].filter((id) => !serverIds.has(id));
    for (const a of server) {
      serverImageMapRef.current.set(a.imageId, a.id);
      serverSeenRef.current.add(a.imageId);
    }
    for (const id of removedIds) {
      serverImageMapRef.current.delete(id);
      serverSeenRef.current.delete(id);
    }
    setImages((prev) => {
      const present = new Set(prev.map((i) => i.id));
      const additions = server
        .filter((a) => !present.has(a.imageId) && !removedWhileUploading.has(a.imageId))
        .map((a): StageImage => ({ id: a.imageId, label: a.label || 'Image', url: a.imageUrl }));
      if (removedIds.length === 0 && additions.length === 0) return prev;
      const kept = removedIds.length > 0 ? prev.filter((i) => !removedIds.includes(i.id)) : prev;
      return additions.length > 0 ? [...kept, ...additions] : kept;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentsSig, ticketId]);

  // Patch attachment URLs in place when the presigned links rotate
  // (review round 1). Same treatment the ORIGINAL capture slot gets
  // from its own patcher above: every ticket refetch carries fresh
  // signatures, and without this a long-open tab's extra images expire
  // and break while the original keeps working. Identity (id, order,
  // active selection) is untouched - only the url field moves.
  const attachmentsUrlSig = (attachments ?? [])
    .map((a) => `${a.imageId}=${a.imageUrl}`)
    .sort()
    .join('|');
  useEffect(() => {
    if (hydratedTicketRef.current !== ticketId) return;
    const byImageId = new Map(attachmentsRef.current.map((a) => [a.imageId, a.imageUrl]));
    setImages((prev) => {
      let changed = false;
      const next = prev.map((img) => {
        const fresh = byImageId.get(img.id);
        if (fresh === undefined || fresh === img.url) return img;
        changed = true;
        return { ...img, url: fresh };
      });
      return changed ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachmentsUrlSig, ticketId]);

  // Upload every image that exists only locally (TOADO-261): fresh
  // carousel adds and pre-migration localStorage extras go through the
  // same path. `imageId` is OUR carousel id, so shapes already tagged
  // with it stay attached, and the endpoint is idempotent per
  // (ticket, imageId) - a retried upload cannot duplicate. On success
  // the no-op setImages nudge re-runs the persistence effect below so
  // the localStorage buffer drops the now-persisted image.
  useEffect(() => {
    if (hydratedTicketRef.current !== ticketId) return;
    // CRITICAL guard (review round: cross-ticket upload). On a cached
    // ticket switch A->B this effect fires in the same commit as the
    // hydration effect - hydratedTicketRef already says B, but the
    // `images` closure is still TICKET A's array, and without this
    // check every A-extra still holding a data: URL would be POSTed
    // to B, durably cross-pollinating tickets. pendingHydrationRef is
    // primed by the hydration effect and stays true for exactly this
    // stale-images commit (the persistence effect below consumes it),
    // so reading it here - without consuming - closes the window.
    if (pendingHydrationRef.current) return;
    const forTicket = ticketId;
    for (const img of images) {
      if (img.id === ORIGINAL_IMAGE_ID) continue;
      if (!img.url || !img.url.startsWith('data:')) continue;
      if (serverImageMapRef.current.has(img.id)) continue;
      if (uploadsInFlight.has(img.id)) continue;
      if (uploadAttemptedRef.current.has(img.id)) continue;
      uploadsInFlight.add(img.id);
      api
        .uploadTicketImage(forTicket, { dataUrl: img.url, label: img.label, imageId: img.id })
        .then((row) => {
          // Removed while we were uploading: the row now exists
          // server-side but the user already said no - delete it
          // rather than confirming it (it would resurrect on the next
          // refetch otherwise).
          if (removedWhileUploading.has(img.id)) {
            removedWhileUploading.delete(img.id);
            api.deleteTicketImage(forTicket, row.id).catch((err) => {
              console.warn('[annotations] cleanup of removed-during-upload attachment failed', err);
            });
            return;
          }
          // Ticket switched while in flight: the upload was for the
          // OLD ticket and is durable there; touching the CURRENT
          // ticket's bookkeeping or images would corrupt it. The old
          // ticket's next open reconciles the row in (the idempotent
          // upload of its still-buffered copy simply returns it).
          if (hydratedTicketRef.current !== forTicket) return;
          serverImageMapRef.current.set(img.id, row.id);
          setImages((prev) => [...prev]);
          // Shapes drawn on this image BEFORE its upload landed were
          // withheld from the shared snapshot by the server's
          // unknown-image filter (teammates could not see the image
          // yet). Now that the attachment exists, nudge a re-save so
          // those shapes reach the server. No-op when none reference
          // this image (the debounced save skips identical stacks via
          // syncedBaseRef reference equality - a fresh array here is
          // cheap and correct either way).
          if (shapesRef.current.some((sh) => sh.imageId === img.id)) {
            setShapes((prev) => [...prev]);
          }
        })
        .catch((err: Error & { status?: number }) => {
          // A marked remove-during-upload whose upload FAILED created
          // no row; clear the marker so it does not sit in the
          // module-scoped set for the whole session (review round 10).
          removedWhileUploading.delete(img.id);
          uploadAttemptedRef.current.add(img.id);
          console.warn('[annotations] attachment upload failed', err);
          // A 4xx is not retryable (unsupported format, over the size
          // cap, at the per-ticket limit) - without saying so, the
          // author believes the image is shared while teammates never
          // see it. Transient failures stay silent and retry next
          // mount, same fail-soft posture as the shape saves.
          if (
            hydratedTicketRef.current === forTicket &&
            err.status !== undefined &&
            err.status >= 400 &&
            err.status < 500
          ) {
            toast.push({
              variant: 'warning',
              title: 'Image not saved to the server',
              description: `"${img.label}" stays visible only in this browser: ${err.message}`,
            });
          }
        })
        .finally(() => {
          uploadsInFlight.delete(img.id);
        });
    }
  }, [images, ticketId, toast]);

  // If `activeImageId` points at an image that no longer exists in the
  // carousel (e.g., the original capture was just dropped by the
  // patcher above), advance to the first remaining image. Empty
  // carousel falls back to '' which the renderer interprets as the
  // "no capture" state. Standalone effect rather than inlining inside
  // setImages so the "did we need to advance?" decision is readable
  // here in one place.
  useEffect(() => {
    if (!activeImageId) return;
    if (images.some((i) => i.id === activeImageId)) return;
    setActiveImageId(images[0]?.id ?? '');
  }, [images, activeImageId]);

  // Close a pending remove-image dialog if the target image vanishes
  // for any reason (ticket switch, programmatic image removal, etc.).
  // Without this, the dialog would keep referencing an id that no
  // longer resolves to anything in the carousel.
  useEffect(() => {
    if (!removeImageTarget) return;
    if (!images.some((i) => i.id === removeImageTarget)) {
      setRemoveImageTarget(null);
    }
  }, [images, removeImageTarget]);

  useEffect(() => {
    if (hydratedTicketRef.current !== ticketId) return;
    if (pendingHydrationRef.current) {
      // First invocation after a load primed the guard — the images
      // state hasn't propagated yet, so whatever's in `images` right
      // now is stale. Clear the flag and skip.
      pendingHydrationRef.current = false;
      return;
    }
    // Only images the server does NOT have yet (TOADO-261): the buffer
    // exists for upload-in-flight / upload-failed images, so persisted
    // ones drop out of it - which is also what retires the pre-feature
    // legacy extras once their migration upload lands.
    const extras = images.filter(
      (i) => i.id !== ORIGINAL_IMAGE_ID && !serverImageMapRef.current.has(i.id),
    );
    try {
      localStorage.setItem(`vd-annot-imgs-${ticketId}`, JSON.stringify(extras));
    } catch {
      // QuotaExceededError most commonly — data URLs for screenshots
      // can push past the 5MB per-origin limit quickly. Far rarer now
      // that only unconfirmed images buffer here; the server upload is
      // the durable home.
    }
  }, [images, ticketId]);

  // Draw the active image's shape stack plus any in-progress drag.
  // `visibleActiveShape` already filters by activeImageId (see its
  // derivation comment for why that defense-in-depth matters).
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (canvas.width !== NATURAL_W || canvas.height !== NATURAL_H) {
      canvas.width = NATURAL_W;
      canvas.height = NATURAL_H;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const all = visibleActiveShape ? [...activeImageShapes, visibleActiveShape] : activeImageShapes;
    for (const s of all) drawShape(ctx, s, s.id === selectedId);
    // Bug markers decode their sprite asynchronously and draw a hand-drawn
    // glyph until it lands. This effect only reruns when a shape or the
    // selection changes, so on a ticket nobody is editing the generic bug
    // would be what the reader sees for good. Asking to be told costs nothing
    // once the sprite is there: the subscription returns immediately.
    return onBugSpriteReady(() => setSpriteGeneration((value) => value + 1));
  }, [activeImageShapes, visibleActiveShape, selectedId, spriteGeneration]);

  // Auto-fit the image to the largest size that fits the viewing area.
  //
  // Two things used to go wrong here:
  //   1. A single useLayoutEffect tried to measure the scroll container on
  //      mount. If the container was still laying out (flex parent not
  //      measured yet) `getBoundingClientRect()` returned 0×0 and the
  //      effect bailed silently — image stayed at 100% zoom and visibly
  //      smaller than it could be.
  //   2. Switching the carousel to a different image didn't re-fit.
  //
  // A ResizeObserver catches the first real layout pass (and sidebar
  // toggles / window resizes along the way). We also refit when the
  // active image changes. Once the user manually adjusts zoom — via the
  // wheel or the +/- buttons — `autoFitRef` flips off and the refit
  // stops fighting them.
  const autoFitRef = useRef(true);
  // The picture's own shape, read as it loads. The canvas space is a fixed box and a screenshot
  // of another shape sits in it with empty bands (`object-fit: contain`), so fitting the box left
  // a widescreen capture at about 80% when it could fill the screen (Jason, 23 Oct). Fit is the
  // largest size at which the picture itself shows whole; the bands may then run off the edges.
  const [pictureAspect, setPictureAspect] = useState<number | null>(null);
  const fitZoomFor = useCallback((r: { width: number; height: number }) => {
    const box = NATURAL_W / NATURAL_H;
    const aspect = pictureAspect && Number.isFinite(pictureAspect) ? pictureAspect : box;
    const width = aspect >= box ? NATURAL_W : NATURAL_H * aspect;
    const height = aspect >= box ? NATURAL_W / aspect : NATURAL_H;
    return clamp(Math.min((r.width - PAN_PAD * 2) / width, (r.height - PAN_PAD * 2) / height), MIN_ZOOM, MAX_ZOOM);
  }, [pictureAspect]);
  /** The picture in the middle of the view, its bands off either edge alike. */
  const centreView = (el: HTMLElement) => requestAnimationFrame(() => {
    el.scrollLeft = (el.scrollWidth - el.clientWidth) / 2;
    el.scrollTop = (el.scrollHeight - el.clientHeight) / 2;
  });
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const fit = () => {
      if (!autoFitRef.current) return;
      const r = el.getBoundingClientRect();
      if (!r.width || !r.height) return;
      setZoom(fitZoomFor(r));
      centreView(el);
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    return () => ro.disconnect();
  }, [activeImageId, fitZoomFor]);

  // Wheel zoom anchored at mouse. The state update + scroll
  // adjustment must happen in the SAME paint to avoid a one-frame
  // jump where the scaler is at the new size but the scroll position
  // is still the old value (or vice versa). flushSync forces React
  // to commit the new zoom before we read/write scrollLeft.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      // Image-space coordinates of the point currently under the cursor.
      const nx = (el.scrollLeft + mx) / zoom;
      const ny = (el.scrollTop + my) / zoom;
      const factor = Math.exp(-e.deltaY * 0.0015);
      const next = clamp(zoom * factor, MIN_ZOOM, MAX_ZOOM);
      if (Math.abs(next - zoom) < 0.0005) return;
      autoFitRef.current = false;
      flushSync(() => setZoom(next));
      // Now the .scaler is at its new size — adjust scroll so the
      // image-space point (nx, ny) lands back under the cursor.
      el.scrollLeft = nx * next - mx;
      el.scrollTop = ny * next - my;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoom]);

  // Centered zoom for the +/- buttons. Same math as the wheel handler
  // but anchors on the viewport center rather than the cursor — gives
  // the user a stable focal point when they pump the buttons. Without
  // this, button zoom snaps the image because nothing reanchors the
  // scroll position.
  const zoomBy = (factor: number) => {
    const el = scrollRef.current;
    autoFitRef.current = false;
    if (!el) {
      setZoom((z) => clamp(z * factor, MIN_ZOOM, MAX_ZOOM));
      return;
    }
    const rect = el.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    const nx = (el.scrollLeft + cx) / zoom;
    const ny = (el.scrollTop + cy) / zoom;
    const next = clamp(zoom * factor, MIN_ZOOM, MAX_ZOOM);
    if (Math.abs(next - zoom) < 0.0005) return;
    flushSync(() => setZoom(next));
    el.scrollLeft = nx * next - cx;
    el.scrollTop = ny * next - cy;
  };

  const focusAnnotationToolbar = () => {
    pendingToolbarFocusRef.current = true;
  };
  const focusPinTarget = (pinId: string) => {
    pendingPinFocusRef.current = pinId;
  };
  useLayoutEffect(() => {
    const pinId = pendingPinFocusRef.current;
    if (!pinId || selectedId !== null) return;
    // This effect runs after React has committed the deselected DOM, so the
    // transparent keyboard target exists. A bare rAF can race that commit.
    const target = Array.from(
      stageRef.current?.querySelectorAll<HTMLButtonElement>('[data-pin-id]') ?? [],
    ).find((element) => element.dataset.pinId === pinId);
    pendingPinFocusRef.current = null;
    (target ?? toolbarRef.current)?.focus();
  }, [activeImageId, selectedId, shapes]);
  useLayoutEffect(() => {
    if (!pendingToolbarFocusRef.current || selectedId !== null) return;
    pendingToolbarFocusRef.current = false;
    const toolbar = toolbarRef.current;
    const firstEnabled = toolbar?.querySelector<HTMLButtonElement>('button:not([disabled])');
    (firstEnabled ?? toolbar)?.focus();
  }, [selectedId, shapes]);
  const cancelActiveDrag = () => {
    const drag = dragModeRef.current;
    if (drag) {
      const latestBaseShape = syncedBaseRef.current.find((shape) => shape.id === drag.id);
      const replacement = cancelledDragShape(
        drag.startShape,
        drag.startBaseShape,
        latestBaseShape,
        dragBaseChanged(
          drag.startVersion,
          lastAnnotationVersionRef.current,
          drag.startBaseShape,
          latestBaseShape,
        ),
      );
      setShapes((list) =>
        replacement
          ? list.map((shape) => (shape.id === drag.id ? replacement : shape))
          : list.filter((shape) => shape.id !== drag.id),
      );
    }
    setDragMode(null);
  };

  useShortcutMap({
    v: () => {
      setTool('pan');
      setSelectedId(null);
    },
    p: () => {
      setTool('pen');
      setSelectedId(null);
    },
    h: () => {
      setTool('highlighter');
      setSelectedId(null);
    },
    c: () => {
      setTool('circle');
      setSelectedId(null);
    },
    a: () => {
      setTool('arrow');
      setSelectedId(null);
    },
    r: () => {
      setTool('redact');
      setSelectedId(null);
    },
    b: () => {
      setTool('blur');
      setSelectedId(null);
    },
    e: () => {
      setTool('eraser');
      setSelectedId(null);
    },
    t: () => {
      // Pressing T while Text is already active opens the defaults editor —
      // keyboard parity with double-clicking the Text tool button.
      if (tool === 'text') {
        setTextModalIsDefaults(true);
        setTextModalTarget(null);
        setTextModalStyleOnly(false);
      } else {
        setTool('text');
        setSelectedId(null);
      }
    },
    Escape: () => {
      const selected = selectedId ? shapes.find((shape) => shape.id === selectedId) : undefined;
      if (selected?.kind === 'pin') focusPinTarget(selected.id);
      else if (selected) focusAnnotationToolbar();
      setActiveShape(null);
      setSelectedId(null);
      cancelActiveDrag();
    },
    Delete: () => {
      if (selectedId) {
        const selected = shapes.find((shape) => shape.id === selectedId);
        setShapes((s) => s.filter((x) => x.id !== selectedId));
        setSelectedId(null);
        setDragMode(null);
        if (selected?.kind === 'pin') {
          setPinAnnouncement((current) => ({
            sequence: (current?.sequence ?? 0) + 1,
            text: 'Screenshot pin deleted.',
          }));
        }
        focusAnnotationToolbar();
      }
    },
  });

  // Click the Text tool while it's already active → open the defaults editor.
  const selectTool = (next: AnnotTool) => {
    if (next === 'text' && tool === 'text') {
      setTextModalIsDefaults(true);
      setTextModalTarget(null);
      setTextModalStyleOnly(false);
    } else {
      setTool(next);
      setSelectedId(null);
    }
  };

  const toNatural = (clientX: number, clientY: number): [number, number] => {
    const stage = stageRef.current;
    if (!stage) return [0, 0];
    const rect = stage.getBoundingClientRect();
    return [(clientX - rect.left) / zoom, (clientY - rect.top) / zoom];
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const [x, y] = toNatural(e.clientX, e.clientY);

    // Space-held overrides whatever tool is active — same Figma-style
    // hold-to-pan affordance. Keep the active tool unchanged so
    // releasing space restores it. Once panState is set, the pan
    // continues even if the user lifts space mid-drag (Figma parity).
    if (spaceHeld || tool === 'pan') {
      const el = scrollRef.current;
      if (!el) return;
      setPanState({
        startClientX: e.clientX,
        startClientY: e.clientY,
        startScrollLeft: el.scrollLeft,
        startScrollTop: el.scrollTop,
      });
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      return;
    }

    if (tool === 'eraser') {
      // Only erase shapes on the active image so the eraser can't
      // silently delete shapes that aren't even visible.
      const hit = findLastIndex(shapes, (s) => s.imageId === activeImageId && shapeHit(s, x, y));
      if (hit >= 0) setShapes((s) => s.filter((_, i) => i !== hit));
      return;
    }

    // Hit-testing and selection are scoped to the active image — a shape
    // drawn on image B shouldn't be selectable while looking at image A
    // (its render is filtered out for that image too).
    // Resolve the actual painted top shape once, before applying tool policy.
    // A tool may ignore a top shape, but it must never select a covered lower
    // pin through that visible shape.
    const topmostHit = topmostHitShape(shapes, activeImageId, x, y);
    // Text tool: click inside an existing textBox on this image → select
    // it; otherwise start a new drag.
    if (tool === 'text') {
      const hitTextOrPin =
        topmostHit?.kind === 'textBox' || (!e.altKey && topmostHit?.kind === 'pin')
          ? topmostHit
          : undefined;
      if (hitTextOrPin) {
        setSelectedId(hitTextOrPin.id);
        return;
      }
      setSelectedId(null);
      setActiveShape({
        id: shapeId(),
        imageId: activeImageId,
        kind: 'textBox',
        x,
        y,
        w: 0,
        h: 0,
        text: '',
        variant: textDefaults.variant,
        style: textDefaults.style,
      });
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      return;
    }

    // Redact tool: click inside an existing redact rect on this image →
    // select it; otherwise start a new drag.
    if (tool === 'redact') {
      const hitRedactOrPin =
        topmostHit?.kind === 'redact' || (!e.altKey && topmostHit?.kind === 'pin')
          ? topmostHit
          : undefined;
      if (hitRedactOrPin) {
        setSelectedId(hitRedactOrPin.id);
        return;
      }
      setSelectedId(null);
      setActiveShape({ id: shapeId(), imageId: activeImageId, kind: 'redact', x, y, w: 0, h: 0 });
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      return;
    }

    // Blur tool: same shape + selection model as redact, different
    // fill (translucent backdrop-blur instead of solid black). The
    // user picks blur when they want to obscure content without
    // making it look heavy-handedly redacted (faces, names, etc).
    if (tool === 'blur') {
      const hitBlurOrPin =
        topmostHit?.kind === 'blur' || (!e.altKey && topmostHit?.kind === 'pin')
          ? topmostHit
          : undefined;
      if (hitBlurOrPin) {
        setSelectedId(hitBlurOrPin.id);
        return;
      }
      setSelectedId(null);
      setActiveShape({ id: shapeId(), imageId: activeImageId, kind: 'blur', x, y, w: 0, h: 0 });
      (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
      return;
    }

    // Other drawing tools have no selectable shape of their own, so a pin
    // under the pointer wins before beginning a new stroke/circle/arrow.
    const hitPin = !e.altKey && topmostHit?.kind === 'pin' ? topmostHit : undefined;
    if (hitPin) {
      setSelectedId(hitPin.id);
      return;
    }

    setSelectedId(null);

    if (tool === 'pen' || tool === 'highlighter') {
      setActiveShape({
        id: shapeId(),
        imageId: activeImageId,
        kind: 'stroke',
        tool,
        color,
        width: tool === 'highlighter' ? 14 : 3,
        points: [[x, y]],
      });
    } else if (tool === 'circle') {
      setActiveShape({
        id: shapeId(),
        imageId: activeImageId,
        kind: 'circle',
        color,
        x,
        y,
        w: 0,
        h: 0,
      });
    } else if (tool === 'arrow') {
      setActiveShape({
        id: shapeId(),
        imageId: activeImageId,
        kind: 'arrow',
        color,
        x1: x,
        y1: y,
        x2: x,
        y2: y,
      });
    }

    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (panState) {
      const el = scrollRef.current;
      if (!el) return;
      el.scrollLeft = panState.startScrollLeft - (e.clientX - panState.startClientX);
      el.scrollTop = panState.startScrollTop - (e.clientY - panState.startClientY);
      return;
    }
    if (!activeShape) return;
    const [x, y] = toNatural(e.clientX, e.clientY);

    if (activeShape.kind === 'stroke') {
      setActiveShape({ ...activeShape, points: [...activeShape.points, [x, y]] });
    } else if (
      activeShape.kind === 'circle' ||
      activeShape.kind === 'redact' ||
      activeShape.kind === 'blur' ||
      activeShape.kind === 'textBox'
    ) {
      setActiveShape({ ...activeShape, w: x - activeShape.x, h: y - activeShape.y });
    } else if (activeShape.kind === 'arrow') {
      setActiveShape({ ...activeShape, x2: x, y2: y });
    }
  };

  const onPointerUp = () => {
    if (panState) {
      setPanState(null);
      return;
    }
    if (!activeShape) return;
    if (activeShape.kind === 'textBox') {
      const x = Math.min(activeShape.x, activeShape.x + activeShape.w);
      const y = Math.min(activeShape.y, activeShape.y + activeShape.h);
      let w = Math.abs(activeShape.w);
      let h = Math.abs(activeShape.h);
      // Treat a click (no drag) or a tiny drag as a request for the default
      // rectangle. The user can still resize via the corner handles.
      if (w < 30 || h < 20) {
        w = Math.max(w, 220);
        h = Math.max(h, 64);
      }
      // Drop the shape in immediately — no modal. The overlay renders an
      // inline textarea that auto-focuses for typing. Tagged to the
      // active image so it only appears alongside its sibling shapes.
      const id = shapeId();
      const newShape: Shape = {
        id,
        imageId: activeImageId,
        kind: 'textBox',
        x,
        y,
        w,
        h,
        text: '',
        variant: textDefaults.variant,
        style: textDefaults.style,
      };
      setShapes((s) => [...s, newShape]);
      setRedoStack([]);
      setSelectedId(id);
      setActiveShape(null);
      return;
    }
    setShapes((s) => [...s, activeShape]);
    setRedoStack([]);
    setActiveShape(null);
  };

  // Drag selected rect-shape (textBox, redact, or blur) via pointer events on the overlay.
  const startDrag = (e: ReactPointerEvent, id: string, kind: RectDragKind) => {
    e.stopPropagation();
    if (e.button !== 0) return;
    const shape = shapes.find((s) => s.id === id);
    if (!shape || (shape.kind !== 'textBox' && shape.kind !== 'redact' && shape.kind !== 'blur'))
      return;
    const [x, y] = toNatural(e.clientX, e.clientY);
    setDragMode({
      shapeType: 'rect',
      id,
      kind,
      startNatX: x,
      startNatY: y,
      startShape: shape,
      startBaseShape: syncedBaseRef.current.find((baseShape) => baseShape.id === id),
      startVersion: lastAnnotationVersionRef.current,
    });
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const startPinDrag = (e: ReactPointerEvent, id: string) => {
    if (e.button !== 0) return;
    // Let Alt+pointer-down bubble to the stage. Its tool handler ignores the
    // pin hit while Alt is held and begins the requested drawing underneath.
    if (e.altKey) {
      setSelectedId(null);
      return;
    }
    e.stopPropagation();
    const shape = shapes.find((s) => s.id === id);
    if (!shape || shape.kind !== 'pin') return;
    const [x, y] = toNatural(e.clientX, e.clientY);
    setDragMode({
      shapeType: 'pin',
      id,
      kind: 'move',
      startNatX: x,
      startNatY: y,
      startShape: shape,
      startBaseShape: syncedBaseRef.current.find((baseShape) => baseShape.id === id),
      startVersion: lastAnnotationVersionRef.current,
    });
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onOverlayPointerMove = (e: ReactPointerEvent) => {
    const drag = dragModeRef.current;
    if (!drag) return;
    const [x, y] = toNatural(e.clientX, e.clientY);
    const dx = x - drag.startNatX;
    const dy = y - drag.startNatY;
    setShapes((list) =>
      list.map((s) => {
        if (s.id !== drag.id) return s;
        if (drag.shapeType === 'pin') {
          if (s.kind !== 'pin') return s;
          const start = drag.startShape;
          const next = clampPinTip(start.x + dx, start.y + dy, NATURAL_W, NATURAL_H);
          return { ...s, ...next };
        }
        if (s.kind !== 'textBox' && s.kind !== 'redact' && s.kind !== 'blur') return s;
        const start = drag.startShape;
        if (drag.kind === 'move') {
          return {
            ...s,
            x: clamp(start.x + dx, 0, NATURAL_W - Math.abs(start.w)),
            y: clamp(start.y + dy, 0, NATURAL_H - Math.abs(start.h)),
          };
        }
        // resize handles: adjust one corner, keep opposite anchored
        const x1 = start.x,
          y1 = start.y,
          x2 = start.x + start.w,
          y2 = start.y + start.h;
        let nx1 = x1,
          ny1 = y1,
          nx2 = x2,
          ny2 = y2;
        if (drag.kind.includes('l')) nx1 = x1 + dx;
        if (drag.kind.includes('r')) nx2 = x2 + dx;
        if (drag.kind.includes('t')) ny1 = y1 + dy;
        if (drag.kind.includes('b')) ny2 = y2 + dy;
        return {
          ...s,
          x: Math.min(nx1, nx2),
          y: Math.min(ny1, ny2),
          w: Math.abs(nx2 - nx1),
          h: Math.abs(ny2 - ny1),
        };
      }),
    );
  };
  const onOverlayPointerUp = () => setDragMode(null);

  // Undo / redo / clear are scoped to the active image. Mixing them
  // across images would mean clicking undo on image B could pop a
  // shape off image A — invisible feedback from the user's
  // perspective, and effectively a data-loss bug. The redo stack
  // remains a single global array but is filtered by image when
  // consumed so shapes restored from it land back on their original
  // image. Shapes from non-active images stay untouched.
  const undo = () => {
    setShapes((s) => {
      // undoTargetIndex skips unknown kinds (PRD 8.2 round-trip rule):
      // undoing a shape this build cannot render would be an invisible
      // deletion. The semantics live in @vd/annotation-core so every
      // surface shares - and tests - them once.
      const lastIdx = undoTargetIndex(s, activeImageId);
      if (lastIdx < 0) return s;
      const last = s[lastIdx]!;
      setRedoStack((r) => [...r, last]);
      return [...s.slice(0, lastIdx), ...s.slice(lastIdx + 1)];
    });
  };
  const redo = () => {
    setRedoStack((r) => {
      let lastIdx = -1;
      for (let i = r.length - 1; i >= 0; i--) {
        if (r[i]?.imageId === activeImageId) {
          lastIdx = i;
          break;
        }
      }
      if (lastIdx < 0) return r;
      const last = r[lastIdx]!;
      setShapes((s) => [...s, last]);
      return [...r.slice(0, lastIdx), ...r.slice(lastIdx + 1)];
    });
  };
  const clearAll = () => {
    // Only clear shapes belonging to the active image — matches the
    // confirm-dialog copy ("Removes every pen stroke, shape,
    // redaction, and text annotation on this image"). Shapes on
    // other images remain intact. The redo stack gets pruned to the
    // same scope so a subsequent redo doesn't surface a cleared
    // shape back from an "empty" image.
    // clearImageShapes preserves unknown-kind shapes (PRD 8.2): the
    // dialog promises to remove what the user can see on this image,
    // and a newer client's shapes are invisible here.
    setShapes((s) => clearImageShapes(s, activeImageId));
    setRedoStack((r) => clearImageShapes(r, activeImageId));
    setActiveShape(null);
    setSelectedId(null);
    // localStorage gets re-written by the persistence effect once
    // the setShapes commit lands, so explicit removeItem here would
    // race with that write. The next save persists the filtered set.
    setShowClearConfirm(false);
  };

  /**
   * Remove an uploaded image from the carousel and clean up everything
   * tied to it: shapes, redo entries, transient selection. The
   * "advance off missing" effect handles activeImageId if the user was
   * looking at the removed image. localStorage gets re-written by the
   * existing persistence effects on the next commit.
   *
   * Refuses to remove the original screenshot slot — the original is
   * the underlying ticket asset and isn't user-owned via this UI.
   */
  const removeImage = (id: string) => {
    if (id === ORIGINAL_IMAGE_ID) return;
    // Persisted images delete server-side too (TOADO-261). The IMAGE
    // is removed optimistically (visual feedback), but its SHAPES are
    // pruned only after the server confirms (review round 2): shapes
    // for an absent image never render, so keeping them until the
    // DELETE lands is invisible - whereas pruning eagerly meant a
    // transient delete failure fed a shape-less snapshot into the
    // debounced save, permanently destroying the image's annotations
    // in the shared server snapshot while the image itself came back.
    const attachmentId = serverImageMapRef.current.get(id);
    const pruneShapes = () => {
      setShapes((s) => s.filter((x) => x.imageId !== id));
      setRedoStack((r) => r.filter((x) => x.imageId !== id));
    };
    if (attachmentId) {
      serverImageMapRef.current.delete(id);
      serverSeenRef.current.delete(id);
      const forTicket = ticketId;
      api
        .deleteTicketImage(ticketId, attachmentId)
        .then(() => {
          // The SERVER pruned this image's shapes from the shared
          // snapshot inside the DELETE transaction (review round 4) -
          // a client-side snapshot write here was tried and retracted:
          // built from a dispatch-time copy, it could overwrite
          // strokes drawn after dispatch under last-writer-wins. Local
          // pruning is only the still-mounted visual cleanup; after a
          // ticket switch there is nothing left to do.
          if (hydratedTicketRef.current !== forTicket) return;
          pruneShapes();
        })
        .catch((err) => {
          console.warn('[annotations] attachment delete failed', err);
          // Restore the image (review round 1): the reconcile effect
          // is keyed on the attachment SET, which a failed delete does
          // not change, so it cannot resurrect this on its own.
          // Shapes were never pruned, so nothing else to restore.
          if (hydratedTicketRef.current !== forTicket) return;
          const row = attachmentsRef.current.find((a) => a.id === attachmentId);
          if (!row) return;
          serverImageMapRef.current.set(row.imageId, row.id);
          serverSeenRef.current.add(row.imageId);
          setImages((prev) =>
            prev.some((i) => i.id === row.imageId)
              ? prev
              : [...prev, { id: row.imageId, label: row.label || 'Image', url: row.imageUrl }],
          );
        });
    } else {
      // Never persisted server-side as far as we know. If an upload is
      // in flight, flag it so the success handler deletes the row it
      // just created instead of confirming it.
      if (uploadsInFlight.has(id)) removedWhileUploading.add(id);
      uploadAttemptedRef.current.add(id);
      pruneShapes();
    }
    setImages((arr) => arr.filter((i) => i.id !== id));
    // Clear transient state that might point at the removed image.
    // selectedId could be a shape on the deleted image; activeShape
    // is tagged with whatever was active before; dragMode references
    // a shape by id which may no longer exist after the cleanup.
    setSelectedId(null);
    setActiveShape(null);
    setDragMode(null);
    setRemoveImageTarget(null);
  };

  // Cmd/Ctrl+Z to undo, Cmd/Ctrl+Shift+Z (or Ctrl+Y) to redo. Refs so
  // the listener doesn't need to re-attach on every render — undo and
  // redo are recreated each render but we always want the latest.
  // Skip when the user is typing in an input/textarea/contenteditable
  // so native text undo continues to work in the title field, comment
  // composer, in-canvas text annotations, etc.
  const undoRef = useRef(undo);
  const redoRef = useRef(redo);
  undoRef.current = undo;
  redoRef.current = redo;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.key === 'z' || e.key === 'Z') {
        e.preventDefault();
        if (e.shiftKey) redoRef.current();
        else undoRef.current();
      } else if (e.key === 'y' || e.key === 'Y') {
        e.preventDefault();
        redoRef.current();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Hold space → pan (Figma-style). Set spaceHeld on keydown, clear on
  // keyup. Skip when focus is in an input/textarea/contenteditable so
  // typing a literal space in the title field, comment composer, or
  // an in-canvas text annotation isn't swallowed. preventDefault on
  // the keydown stops the page-level scroll-on-space.
  useEffect(() => {
    // A video or audio element takes Space to play and pause, and the
    // walkthrough clip plays over this canvas (t-cnb0zy), so it is left alone
    // like a text field: cancelling the key here would cancel that too.
    const isTypingTarget = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      return !!el && (
        el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable
        || el.tagName === 'VIDEO' || el.tagName === 'AUDIO'
      );
    };
    const onDown = (e: KeyboardEvent) => {
      if (e.key !== ' ' && e.code !== 'Space') return;
      if (isTypingTarget(e.target)) return;
      // Repeats fire many keydowns while held — only set state once.
      if (e.repeat) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      setSpaceHeld(true);
    };
    const onUp = (e: KeyboardEvent) => {
      if (e.key !== ' ' && e.code !== 'Space') return;
      if (isTypingTarget(e.target)) return;
      setSpaceHeld(false);
    };
    // Window blur clears the latch — without this, alt-tabbing while
    // holding space leaves the canvas stuck in pan mode after the
    // user returns and releases space (the up event already fired
    // outside our window).
    const onBlur = () => setSpaceHeld(false);
    window.addEventListener('keydown', onDown);
    window.addEventListener('keyup', onUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onDown);
      window.removeEventListener('keyup', onUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  // Single entry point for changing the active image. Clears transient
  // state that's pinned to image-space coordinates:
  //   • selectedId — the selection overlay (drag handles + toolbar)
  //     renders at the selected shape's coords; carrying it across
  //     images would land the overlay over the wrong content.
  //   • activeShape — an in-progress drag tagged with the previous
  //     activeImageId. Committing it after the switch would attach
  //     it to the image the user is no longer on.
  //   • dragMode — same reasoning as activeShape; resize-in-progress
  //     should cancel on switch rather than continue against a shape
  //     that's no longer rendered.
  //   • panState — clears any in-flight pan so releasing pointer on
  //     the new image doesn't bleed scroll into the next interaction.
  const selectImage = (id: string) => {
    setActiveImageId(id);
    setSelectedId(null);
    setActiveShape(null);
    setDragMode(null);
    setPanState(null);
  };

  const refit = () => {
    const el = scrollRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Re-enable the auto-fit observer so subsequent container resizes
    // keep the image fitted. Clicking Fit re-expresses user intent.
    autoFitRef.current = true;
    setZoom(fitZoomFor(r));
    centreView(el);
  };

  const stageStyle: CSSProperties = {
    width: NATURAL_W * zoom,
    height: NATURAL_H * zoom,
    cursor: spaceHeld || tool === 'pan' ? (panState ? 'grabbing' : 'grab') : cursorFor(tool, color),
  };

  // Look up the selected shape in the active image's slice so a
  // selection from a previously-viewed image can't render its overlay
  // on top of the current image. Image switches clear `selectedId`
  // via the carousel's onSelect wrapper, but this defends against any
  // path that forgets to (defense in depth — overlay positions are in
  // image-space coords and would land in the wrong spot otherwise).
  const selectedShape = selectedId ? activeImageShapes.find((s) => s.id === selectedId) : null;
  const selectedTextBox = selectedShape?.kind === 'textBox' ? selectedShape : null;
  const selectedPin = isRenderablePinShape(selectedShape) ? selectedShape : null;
  const activePins = activeImageShapes.filter(isRenderablePinShape);
  const keyboardPins = activePins.filter((pin) => pin.id !== selectedPin?.id);
  const effectivePinTabStopId = keyboardPins.some((pin) => pin.id === pinTabStopId)
    ? pinTabStopId
    : (keyboardPins[0]?.id ?? null);
  // Redact and blur share the rect-selection overlay (drag handles +
  // delete button) — they're both axis-aligned rects with no other
  // editable parameters. Same UI, same behavior; the only difference
  // is what fills the rect on screen and in the exported PNG.
  const selectedRect =
    selectedShape?.kind === 'redact' || selectedShape?.kind === 'blur' ? selectedShape : null;
  const selectedRedact = selectedRect;

  const saveTextBox = (data: { text: string; variant: TextVariant; style: TextStyle }) => {
    // Update defaults to the most recent choice.
    setTextDefaults({ variant: data.variant, style: data.style });

    if (textModalIsDefaults) {
      // Only updating defaults — no shape to create.
      setTextModalIsDefaults(false);
      return;
    }

    if (textModalTarget) {
      // Editing an existing shape. In styleOnly mode, `data.text` is seeded
      // from the current shape's text (the text field is hidden), so this
      // spread leaves `.text` unchanged and only updates variant/style.
      setShapes((s) =>
        s.map((x) => (x.id === textModalTarget.id ? { ...textModalTarget, ...data } : x)),
      );
    }
    setTextModalTarget(null);
    setTextModalStyleOnly(false);
  };

  // Live inline text edit — called on every keystroke in the textarea.
  const updateTextBoxText = (id: string, text: string) => {
    setShapes((s) => s.map((x) => (x.id === id && x.kind === 'textBox' ? { ...x, text } : x)));
  };

  const activeImage = images.find((i) => i.id === activeImageId);
  const cover = stageMedia?.cover ?? null;
  const imageHidden = cover?.hidesImage === true;

  return (
    <div className={styles.wrap}>
      <div className={styles.stageArea}>
        <div ref={scrollRef} className={styles.scroll}>
          <div
            ref={stageRef}
            className={styles.stage}
            style={stageStyle}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
          >
            <div
              className={styles.scaler}
              style={{
                width: NATURAL_W,
                height: NATURAL_H,
                transform: `scale(${zoom})`,
                transformOrigin: '0 0',
              }}
            >
              {activeImage?.url ? (
                <img src={activeImage.url} alt={activeImage.label} className={styles.img} onLoad={(event) => setPictureAspect(event.currentTarget.naturalWidth / event.currentTarget.naturalHeight || null)} />
              ) : screenshotUrl && screenshotUrl.startsWith('http') ? (
                <img src={screenshotUrl} alt="Captured page" className={styles.img} onLoad={(event) => setPictureAspect(event.currentTarget.naturalWidth / event.currentTarget.naturalHeight || null)} />
              ) : (
                // No capture attached and no upload yet. We used to render a
                // decorative mock page here, but that looked like a real
                // screenshot — users would reasonably expect it to show as the
                // card thumbnail elsewhere (it couldn't; it's not real data).
                // A plain empty state matches what cards and the carousel
                // show when there's nothing to show.
                <NoCaptureState />
              )}
              {/* Where the person clicked on the captured page, numbered as the Session window
                  numbers them (TOADO-919). On the screenshot only, never on an attachment, inside
                  .scaler so zoom and pan carry them with the picture; under the drawing canvas. */}
              {!activeImage?.url && screenshotUrl && clickMarks && clickMarks.marks.map((mark) => {
                const at = markInBox(mark, clickMarks.viewport, { width: NATURAL_W, height: NATURAL_H });
                return (
                  <span key={`click-mark-${mark.n}`} className={styles.clickMark} style={{ left: at.left, top: at.top }}
                    data-testid="click-mark" aria-label={`Click ${mark.n}`} role="img">{mark.n}</span>
                );
              })}
              {/* Live blur overlays. backdrop-filter blurs whatever is
                  directly behind these <div>s in the DOM, which is the
                  <img> above. They sit INSIDE .scaler so they share the
                  natural-coordinate system + zoom transform, and they
                  paint AFTER the img so they're stacked on top of it.
                  The canvas (which has user pen strokes / circles / etc)
                  is OUTSIDE .scaler and stacks above all of this — so
                  annotations on the canvas stay sharp on top of blurred
                  regions. The placeholder fill drawn by drawShape on
                  the live canvas gives a 1:1 visual indicator of where
                  each blur shape's bounds are; the actual blur happens
                  here via the browser's compositor. On save
                  (rasterizeAnnotatedImage), canvas filter+clip applies
                  the blur to the image data directly. */}
              {/* Blur overlays line up with the same image's blur
                  strokes drawn on the canvas above. visibleActiveShape
                  is the imageId-guarded in-progress drag. */}
              {[...activeImageShapes, visibleActiveShape]
                .filter((s): s is Extract<Shape, { kind: 'blur' }> => !!s && s.kind === 'blur')
                .map((s) => {
                  const left = Math.min(s.x, s.x + s.w);
                  const top = Math.min(s.y, s.y + s.h);
                  const width = Math.abs(s.w);
                  const height = Math.abs(s.h);
                  if (width < 2 || height < 2) return null;
                  const radius = s.radius ?? 8;
                  return (
                    <div
                      key={`blur-overlay-${s.id}`}
                      aria-hidden
                      style={{
                        position: 'absolute',
                        left,
                        top,
                        width,
                        height,
                        backdropFilter: `blur(${radius}px)`,
                        WebkitBackdropFilter: `blur(${radius}px)`,
                        pointerEvents: 'none',
                      }}
                    />
                  );
                })}
            </div>
            <canvas ref={canvasRef} className={styles.canvas} />

            {/* Canvas pixels are not keyboard-addressable. Mirror each pin with
                a transparent DOM target so keyboard and assistive-technology
                users can select the same marker without a pointer. */}
            {keyboardPins.map((pin) => {
              const bounds = pinVisualBounds(pin);
              const scaledWidth = bounds.width * zoom;
              const scaledHeight = bounds.height * zoom;
              const focusWidth = Math.max(24, scaledWidth);
              const focusHeight = Math.max(24, scaledHeight);
              const label =
                pin.style === 'numbered-dot'
                  ? `Select screenshot pin ${pinDisplayNumber(pin.number)}`
                  : `Select screenshot ${pin.style} pin`;
              return (
                <button
                  key={`pin-hit-${pin.id}`}
                  type="button"
                  className={styles.pinHitTarget}
                  data-pin-id={pin.id}
                  tabIndex={effectivePinTabStopId === pin.id ? 0 : -1}
                  style={{
                    left: bounds.left * zoom - (focusWidth - scaledWidth) / 2,
                    top: bounds.top * zoom - (focusHeight - scaledHeight) / 2,
                    width: focusWidth,
                    height: focusHeight,
                  }}
                  onClick={(e) => {
                    // Enter and Space activate this click path even though CSS
                    // routes pointer input through to the canvas below.
                    e.stopPropagation();
                    setSelectedId(pin.id);
                  }}
                  onFocus={() => setPinTabStopId(pin.id)}
                  onKeyDown={(e) => {
                    if (
                      !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(
                        e.key,
                      )
                    )
                      return;
                    e.preventDefault();
                    const currentIndex = keyboardPins.findIndex(
                      (candidate) => candidate.id === pin.id,
                    );
                    const nextIndex =
                      e.key === 'Home'
                        ? 0
                        : e.key === 'End'
                          ? keyboardPins.length - 1
                          : e.key === 'ArrowLeft' || e.key === 'ArrowUp'
                            ? (currentIndex - 1 + keyboardPins.length) % keyboardPins.length
                            : (currentIndex + 1) % keyboardPins.length;
                    const nextId = keyboardPins[nextIndex]?.id;
                    if (!nextId) return;
                    setPinTabStopId(nextId);
                    Array.from(
                      stageRef.current?.querySelectorAll<HTMLButtonElement>('[data-pin-id]') ?? [],
                    )
                      .find((element) => element.dataset.pinId === nextId)
                      ?.focus();
                  }}
                  aria-label={label}
                />
              );
            })}

            {/* Selection overlay for a selected textBox */}
            {selectedTextBox && (
              <TextBoxOverlay
                key={selectedTextBox.id}
                shape={selectedTextBox}
                zoom={zoom}
                onPointerMove={onOverlayPointerMove}
                onPointerUp={onOverlayPointerUp}
                onCancelDrag={cancelActiveDrag}
                onStartDrag={startDrag}
                onTextChange={(text) => updateTextBoxText(selectedTextBox.id, text)}
                onEditStyle={() => {
                  setTextModalTarget(selectedTextBox);
                  setTextModalStyleOnly(true);
                  setTextModalIsDefaults(false);
                }}
                onDelete={() => {
                  setShapes((s) => s.filter((x) => x.id !== selectedTextBox.id));
                  setSelectedId(null);
                  focusAnnotationToolbar();
                }}
                onDeselect={() => {
                  setSelectedId(null);
                  focusAnnotationToolbar();
                }}
              />
            )}

            {/* Selection overlay for a selected redact rectangle */}
            {selectedRedact && (
              <RedactOverlay
                key={selectedRedact.id}
                shape={selectedRedact}
                zoom={zoom}
                onPointerMove={onOverlayPointerMove}
                onPointerUp={onOverlayPointerUp}
                onCancelDrag={cancelActiveDrag}
                onStartDrag={startDrag}
                onDelete={() => {
                  setShapes((s) => s.filter((x) => x.id !== selectedRedact.id));
                  setSelectedId(null);
                  focusAnnotationToolbar();
                }}
              />
            )}

            {/* Screenshot pins move independently of their live-page anchors. */}
            {selectedPin && (
              <PinOverlay
                key={selectedPin.id}
                shape={selectedPin}
                zoom={zoom}
                onPointerMove={onOverlayPointerMove}
                onPointerUp={onOverlayPointerUp}
                onCancelDrag={cancelActiveDrag}
                onStartDrag={startPinDrag}
                onNudge={(dx, dy) => {
                  let applied = { dx: 0, dy: 0 };
                  // Key-repeat can dispatch again before a normal render. Apply
                  // each delta to the functional current shape and commit before
                  // returning the delta used by the live announcement.
                  flushSync(() =>
                    setShapes((list) =>
                      list.map((shape) => {
                        if (shape.id !== selectedPin.id || !isRenderablePinShape(shape)) return shape;
                        const next = clampPinTip(shape.x + dx, shape.y + dy, NATURAL_W, NATURAL_H);
                        applied = { dx: next.x - shape.x, dy: next.y - shape.y };
                        return applied.dx === 0 && applied.dy === 0 ? shape : { ...shape, ...next };
                      }),
                    ),
                  );
                  return applied;
                }}
                onAnnounce={(text) =>
                  setPinAnnouncement((current) => ({
                    sequence: (current?.sequence ?? 0) + 1,
                    text,
                  }))
                }
                onDeselect={() => {
                  setSelectedId(null);
                  cancelActiveDrag();
                  focusPinTarget(selectedPin.id);
                }}
                onDelete={() => {
                  setShapes((s) => s.filter((x) => x.id !== selectedPin.id));
                  setSelectedId(null);
                  setDragMode(null);
                  setPinAnnouncement((current) => ({
                    sequence: (current?.sequence ?? 0) + 1,
                    text: 'Screenshot pin deleted.',
                  }));
                  focusAnnotationToolbar();
                }}
              />
            )}
          </div>
        </div>
        {cover && (
          <div className={imageHidden ? styles.coverFull : styles.coverBar} data-testid="stage-cover">
            {cover.node}
          </div>
        )}
      </div>

      <span
        className={styles.srOnly}
        aria-live="polite"
        aria-atomic="true"
        data-testid="pin-announcement"
      >
        {pinAnnouncement && (
          <span
            key={pinAnnouncement.sequence}
            data-announcement-sequence={pinAnnouncement.sequence}
          >
            {pinAnnouncement.text}
          </span>
        )}
      </span>

      {/* Zooming an image nobody can see would do nothing visible. */}
      <div className={styles.zoomControls} style={imageHidden ? { visibility: 'hidden' } : undefined}>
        <Tooltip content="Zoom out">
          <button onClick={() => zoomBy(0.9)}>
            <ZoomOut size={14} />
          </button>
        </Tooltip>
        <span className={styles.zoomLabel}>{Math.round(zoom * 100)}%</span>
        <Tooltip content="Zoom in">
          <button onClick={() => zoomBy(1.1)}>
            <ZoomIn size={14} />
          </button>
        </Tooltip>
        <Tooltip content="Fit">
          <button onClick={refit}>
            <Maximize2 size={14} />
          </button>
        </Tooltip>
      </div>

      <div
        style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between' }}
      >
        <ImageCarousel
          images={images}
          // No image is the one on show while the clip covers it.
          activeId={imageHidden ? '' : activeImageId}
          onSelect={(id) => {
            stageMedia?.onShowImage();
            selectImage(id);
          }}
          afterFirst={stageMedia?.tile}
          onAdd={(added) => {
            if (added.length === 0) return;
            setImages((arr) => [...arr, ...added]);
            // Jump to the first newly-added image. Predictable
            // ordering (first picker entry, not last) so a multi-
            // upload puts the user at the start of their batch.
            // Same teardown as a manual switch — see selectImage.
            selectImage(added[0]!.id);
          }}
          onRemove={(id) => {
            // Originals are gated by the carousel itself (it doesn't
            // render the × on 'original'). Defense in depth here too.
            if (id === ORIGINAL_IMAGE_ID) return;
            const hasShapes = shapes.some((s) => s.imageId === id);
            if (hasShapes) {
              setRemoveImageTarget(id);
              return;
            }
            removeImage(id);
          }}
        />
        {carouselRightSlot && <div style={{ flexShrink: 0 }}>{carouselRightSlot}</div>}
      </div>

      <div ref={toolbarRef} className={styles.toolbar} tabIndex={-1} aria-label="Annotation tools">
        <AnnotBar
          tool={tool}
          color={color}
          // A drawing tool brings the image back to draw on.
          onSelectTool={(next) => {
            if (imageHidden) stageMedia?.onShowImage();
            selectTool(next);
          }}
          onSelectColor={setColor}
          onUndo={undo}
          onRedo={redo}
          onClear={() => setShowClearConfirm(true)}
          canUndo={hasEditableShapes(shapes, activeImageId)}
          canRedo={activeImageRedoStack.length > 0}
          canClear={hasEditableShapes(shapes, activeImageId)}
        />
        {tool === 'text' && (
          <div className={styles.textHint}>
            <Settings2 size={11} />
            <span>
              Click <strong>T</strong> again to edit defaults · click a text box to select
            </span>
          </div>
        )}
      </div>

      <TextBoxModal
        open={!!textModalTarget || textModalIsDefaults}
        onClose={() => {
          setTextModalTarget(null);
          setTextModalIsDefaults(false);
          setTextModalStyleOnly(false);
        }}
        onSave={saveTextBox}
        initial={
          textModalTarget
            ? {
                text: textModalTarget.text,
                variant: textModalTarget.variant,
                style: textModalTarget.style,
              }
            : { variant: textDefaults.variant, style: textDefaults.style, text: '' }
        }
        defaultsMode={textModalIsDefaults}
        styleOnly={textModalStyleOnly}
      />

      <ConfirmDialog
        open={showClearConfirm}
        title="Clear all annotations?"
        confirmLabel="Clear all"
        onCancel={() => setShowClearConfirm(false)}
        onConfirm={clearAll}
      >
        Removes every pen stroke, shape, redaction, and text annotation on this image. This can’t be
        undone.
      </ConfirmDialog>

      <ConfirmDialog
        open={!!removeImageTarget}
        title="Remove image?"
        confirmLabel="Remove"
        onCancel={() => setRemoveImageTarget(null)}
        onConfirm={() => removeImageTarget && removeImage(removeImageTarget)}
      >
        {removeImageTarget &&
          (() => {
            const target = images.find((i) => i.id === removeImageTarget);
            const count = shapes.filter((s) => s.imageId === removeImageTarget).length;
            return (
              <>
                Removes <strong>{target?.label ?? 'this image'}</strong> and its {count} annotation
                {count === 1 ? '' : 's'}. This can’t be undone.
              </>
            );
          })()}
      </ConfirmDialog>
    </div>
  );
}

// ---- Confirm dialog ------------------------------------------------------
// Small modal used for destructive operations (clear-all, remove image).
// Co-located with the only consumers — if other features later want the
// same pattern it should be promoted to @vd/ui. Backdrop click cancels;
// ESC isn't wired (matches existing TextBoxModal behavior; revisit when
// adding global keyboard handling to dialogs).

interface ConfirmDialogProps {
  open: boolean;
  title: string;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  children: ReactNode;
}

function ConfirmDialog({
  open,
  title,
  confirmLabel,
  onConfirm,
  onCancel,
  children,
}: ConfirmDialogProps) {
  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.35)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 200,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          padding: 20,
          maxWidth: 380,
          boxShadow: 'var(--sh-xl)',
        }}
      >
        <h3 style={{ fontSize: 16, marginBottom: 6 }}>{title}</h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>{children}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              color: 'var(--text-secondary)',
              background: 'transparent',
            }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '8px 12px',
              borderRadius: 6,
              background: 'var(--danger)',
              color: '#fff',
              fontWeight: 500,
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- Selection overlay for text boxes ------------------------------------

interface TextBoxOverlayProps {
  shape: Extract<Shape, { kind: 'textBox' }>;
  zoom: number;
  onPointerMove: React.PointerEventHandler<HTMLDivElement>;
  onPointerUp: React.PointerEventHandler<HTMLDivElement>;
  onCancelDrag: () => void;
  onStartDrag: (e: ReactPointerEvent<HTMLDivElement>, id: string, kind: RectDragKind) => void;
  onTextChange: (text: string) => void;
  onEditStyle: () => void;
  onDelete: () => void;
  onDeselect: () => void;
}

function TextBoxOverlay({
  shape,
  zoom,
  onPointerMove,
  onPointerUp,
  onCancelDrag,
  onStartDrag,
  onTextChange,
  onEditStyle,
  onDelete,
  onDeselect,
}: TextBoxOverlayProps) {
  const x = Math.min(shape.x, shape.x + shape.w);
  const y = Math.min(shape.y, shape.y + shape.h);
  const w = Math.abs(shape.w);
  const h = Math.abs(shape.h);

  const overlayStyle: CSSProperties = {
    position: 'absolute',
    left: x * zoom,
    top: y * zoom,
    width: w * zoom,
    height: h * zoom,
    pointerEvents: 'auto',
  };

  const toolbarBelow = y * zoom < 48;

  // Match the canvas drawTextBox styling so the inline editor looks like the
  // final rendered annotation. Font + padding mirror drawTextBox; color/
  // shadow/background mirror the style+variant.
  const textareaStyle: CSSProperties = {
    // Font size scales with zoom so the textarea text appears the same size
    // as the canvas-rendered text would at this zoom level.
    fontSize: 18 * zoom,
    lineHeight: `${22 * zoom}px`,
    padding: 10 * zoom,
    fontFamily: 'Inter, sans-serif',
    fontWeight: 600,
    ...stylePresentation(shape.style, shape.variant),
  };

  return (
    <div
      className={styles.selectOverlay}
      style={overlayStyle}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onCancelDrag}
      onLostPointerCapture={onCancelDrag}
    >
      {/* Move affordance — a ring hugging the blue border. Only the edges
          respond to pointer-down for dragging the shape; the center area is
          owned by the textarea below so clicks there focus the editor. */}
      <div
        className={styles.selectMoveRing}
        onPointerDown={(e) => onStartDrag(e, shape.id, 'move')}
        title="Drag edge to move"
      />

      {/* Inline text editor. autoFocus fires once on mount; the parent keys
          this component on shape.id so a newly-created box autofocuses. */}
      <textarea
        className={styles.selectTextEditor}
        style={textareaStyle}
        value={shape.text}
        placeholder="Type here…"
        autoFocus
        onChange={(e) => onTextChange(e.target.value)}
        onKeyDown={(e) => {
          // Escape blurs + deselects so the user can go back to using tools.
          if (e.key === 'Escape') {
            e.preventDefault();
            (e.currentTarget as HTMLTextAreaElement).blur();
            onDeselect();
          }
        }}
        onPointerDown={(e) => e.stopPropagation()}
      />

      {(['tl', 'tr', 'bl', 'br'] as const).map((corner) => (
        <div
          key={corner}
          className={cn(styles.selectHandle, styles[`h-${corner}`])}
          onPointerDown={(e) => onStartDrag(e, shape.id, `resize-${corner}` as const)}
        />
      ))}

      <div
        className={cn(styles.selectToolbar, toolbarBelow && styles.selectToolbarBelow)}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <Tooltip content="Edit text style (color · sticky)">
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Pencil size={12} />}
            onClick={(e) => {
              e.stopPropagation();
              onEditStyle();
            }}
          >
            Style
          </Button>
        </Tooltip>
        <Tooltip content="Drag edge ring to move">
          <span className={styles.selectHint}>
            <Move size={12} />
          </span>
        </Tooltip>
        <Tooltip content="Delete annotation">
          <button
            type="button"
            className={styles.selectDelete}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDelete();
            }}
            aria-label="Delete"
          >
            <Trash2 size={12} />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}

// ---- Selection overlay for redact + blur rectangles ----------------------
// Same overlay used for both — both are axis-aligned rects with no other
// editable parameters. Drag handles + delete button only; their fill
// behavior differs but the selection UI doesn't.

interface RedactOverlayProps {
  shape: Extract<Shape, { kind: 'redact' | 'blur' }>;
  zoom: number;
  onPointerMove: React.PointerEventHandler<HTMLDivElement>;
  onPointerUp: React.PointerEventHandler<HTMLDivElement>;
  onCancelDrag: () => void;
  onStartDrag: (e: ReactPointerEvent<HTMLDivElement>, id: string, kind: RectDragKind) => void;
  onDelete: () => void;
}

function RedactOverlay({
  shape,
  zoom,
  onPointerMove,
  onPointerUp,
  onCancelDrag,
  onStartDrag,
  onDelete,
}: RedactOverlayProps) {
  const x = Math.min(shape.x, shape.x + shape.w);
  const y = Math.min(shape.y, shape.y + shape.h);
  const w = Math.abs(shape.w);
  const h = Math.abs(shape.h);

  const overlayStyle: CSSProperties = {
    position: 'absolute',
    left: x * zoom,
    top: y * zoom,
    width: w * zoom,
    height: h * zoom,
    pointerEvents: 'auto',
  };

  const toolbarBelow = y * zoom < 48;

  return (
    <div
      className={styles.selectOverlay}
      style={overlayStyle}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onCancelDrag}
      onLostPointerCapture={onCancelDrag}
    >
      {/* The redact rect is fully filled black on the canvas — the entire
          interior is the move surface. No textarea to compete with clicks. */}
      <div
        className={styles.selectMoveHandle}
        onPointerDown={(e) => onStartDrag(e, shape.id, 'move')}
        title="Drag to move"
      />

      {(['tl', 'tr', 'bl', 'br'] as const).map((corner) => (
        <div
          key={corner}
          className={cn(styles.selectHandle, styles[`h-${corner}`])}
          onPointerDown={(e) => onStartDrag(e, shape.id, `resize-${corner}` as const)}
        />
      ))}

      <div
        className={cn(styles.selectToolbar, toolbarBelow && styles.selectToolbarBelow)}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <Tooltip content="Drag to move · drag corners to resize">
          <span className={styles.selectHint}>
            <Move size={12} />
          </span>
        </Tooltip>
        <Tooltip content={shape.kind === 'blur' ? 'Delete blur' : 'Delete redaction'}>
          <button
            type="button"
            className={styles.selectDelete}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              onDelete();
            }}
            aria-label="Delete"
          >
            <Trash2 size={12} />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}

// Inline-editor visual presentation — mirrors canvas drawTextBox so the
// textarea blends seamlessly with how the shape will render once deselected.
interface PinOverlayProps {
  shape: PinShape;
  zoom: number;
  onPointerMove: React.PointerEventHandler<HTMLDivElement>;
  onPointerUp: React.PointerEventHandler<HTMLDivElement>;
  onCancelDrag: () => void;
  onStartDrag: (e: ReactPointerEvent<HTMLDivElement>, id: string) => void;
  onNudge: (dx: number, dy: number) => { dx: number; dy: number };
  onAnnounce: (text: string) => void;
  onDeselect: () => void;
  onDelete: () => void;
}

function PinOverlay({
  shape,
  zoom,
  onPointerMove,
  onPointerUp,
  onCancelDrag,
  onStartDrag,
  onNudge,
  onAnnounce,
  onDeselect,
  onDelete,
}: PinOverlayProps) {
  const bounds = pinVisualBounds(shape);
  const overlayRef = useRef<HTMLDivElement>(null);
  const pinToolbarRef = useRef<HTMLDivElement>(null);
  const [toolbarSize, setToolbarSize] = useState({ width: 0, clearance: 56 });
  useEffect(() => {
    overlayRef.current?.focus({ preventScroll: true });
  }, [shape.id]);
  useLayoutEffect(() => {
    const toolbar = pinToolbarRef.current;
    if (!toolbar) return;
    const measure = () => {
      const rect = toolbar.getBoundingClientRect();
      const next = { width: Math.ceil(rect.width), clearance: Math.ceil(rect.height) + 8 };
      setToolbarSize((current) =>
        current.width === next.width && current.clearance === next.clearance ? current : next,
      );
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(toolbar);
    return () => observer.disconnect();
  }, [shape.id]);
  const toolbarBelow = bounds.top * zoom < toolbarSize.clearance;
  const overlayLeft = bounds.left * zoom;
  const toolbarLeft =
    toolbarSize.width > 0
      ? clamp(
          (bounds.width * zoom - toolbarSize.width) / 2,
          4 - overlayLeft,
          NATURAL_W * zoom - toolbarSize.width - 4 - overlayLeft,
        )
      : undefined;

  return (
    <div
      ref={overlayRef}
      role="group"
      aria-label="Selected screenshot pin. Drag to move it. The live-page anchor does not move."
      aria-keyshortcuts="ArrowLeft ArrowRight ArrowUp ArrowDown Delete Escape"
      tabIndex={0}
      data-testid="pin-selection-overlay"
      className={styles.pinSelectOverlay}
      style={{
        left: bounds.left * zoom,
        top: bounds.top * zoom,
        width: bounds.width * zoom,
        height: bounds.height * zoom,
      }}
      onPointerDown={(e) => onStartDrag(e, shape.id)}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onCancelDrag}
      onLostPointerCapture={onCancelDrag}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault();
          e.stopPropagation();
          onDeselect();
          return;
        }
        if (e.target !== e.currentTarget) return;
        const amount = e.shiftKey ? 10 : 1;
        let requested: { dx: number; dy: number };
        if (e.key === 'ArrowLeft') requested = { dx: -amount, dy: 0 };
        else if (e.key === 'ArrowRight') requested = { dx: amount, dy: 0 };
        else if (e.key === 'ArrowUp') requested = { dx: 0, dy: -amount };
        else if (e.key === 'ArrowDown') requested = { dx: 0, dy: amount };
        else return;
        const applied = onNudge(requested.dx, requested.dy);
        const direction =
          applied.dx < 0
            ? 'left'
            : applied.dx > 0
              ? 'right'
              : applied.dy < 0
                ? 'up'
                : applied.dy > 0
                  ? 'down'
                  : requested.dx < 0
                    ? 'left'
                    : requested.dx > 0
                      ? 'right'
                      : requested.dy < 0
                        ? 'up'
                        : 'down';
        const appliedAmount = Math.abs(applied.dx || applied.dy);
        const text =
          appliedAmount === 0
            ? `Screenshot pin is already at the ${direction} edge.`
            : `Screenshot pin moved ${direction} ${appliedAmount} pixel${appliedAmount === 1 ? '' : 's'}.`;
        onAnnounce(text);
        e.preventDefault();
      }}
      title="Drag the screenshot marker"
    >
      <div
        ref={pinToolbarRef}
        className={cn(
          styles.selectToolbar,
          styles.pinSelectToolbar,
          toolbarBelow && styles.selectToolbarBelow,
        )}
        style={toolbarLeft === undefined ? undefined : { left: toolbarLeft, transform: 'none' }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <span className={styles.pinSelectHint}>
          <Move size={12} aria-hidden />
          Screenshot marker only. Live-page anchor stays put. Hold Alt to draw through.
        </span>
        <Tooltip content="Delete screenshot pin">
          <button
            type="button"
            className={styles.selectDelete}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            aria-label="Delete screenshot pin"
          >
            <Trash2 size={12} />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}

function stylePresentation(style: TextStyle, variant: TextVariant): CSSProperties {
  const bg = variant === 'sticky' ? '#FEF3C7' : 'transparent';
  if (style === 'red-outline') {
    return {
      background: bg,
      color: '#EF4444',
      textShadow: '0 0 2px #fff, 0 0 2px #fff, 0 0 2px #fff, 0 0 2px #fff',
    };
  }
  if (style === 'red') return { background: bg, color: '#EF4444' };
  if (style === 'black') return { background: bg, color: '#111111' };
  // white
  return { background: bg, color: '#ffffff', textShadow: '0 1px 2px rgba(0,0,0,0.8)' };
}

// ---- helpers ---------------------------------------------------------------

function findLastIndex<T>(arr: T[], pred: (v: T) => boolean): number {
  for (let i = arr.length - 1; i >= 0; i--) {
    const item = arr[i];
    if (item !== undefined && pred(item)) return i;
  }
  return -1;
}

function cursorFor(tool: AnnotTool, color: HighlightColor): string {
  const hex = highlights[color];
  if (tool === 'pen') return buildCursor(penSvg(hex), 2, 22);
  if (tool === 'highlighter') return buildCursor(highlighterSvg(hex), 4, 20);
  if (tool === 'text') return 'crosshair';
  if (tool === 'eraser') return buildCursor(eraserSvg(), 4, 20);
  if (tool === 'pan') return 'grab';
  return 'crosshair';
}

function buildCursor(svg: string, x: number, y: number): string {
  return `url("data:image/svg+xml;utf8,${encodeURIComponent(svg)}") ${x} ${y}, crosshair`;
}

function penSvg(tint: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white" stroke="black" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
<path d="M4 20l3-1 11-11-2-2L5 17l-1 3z" fill="white"/>
<path d="M14 6l4 4"/>
<circle cx="4.5" cy="19.5" r="1.4" fill="${tint}" stroke="${tint}"/>
</svg>`;
}

function highlighterSvg(tint: string): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="26" height="26" viewBox="0 0 26 26" fill="white" stroke="black" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
<path d="M4 22l2-5 13-13 4 4-13 13-5 2z" fill="${tint}" fill-opacity="0.55" stroke="${tint}"/>
<path d="M15 6l4 4"/>
</svg>`;
}

function eraserSvg(): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="white" stroke="black" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">
<path d="M3 20l6-6 7 7H5z" fill="#FCA5A5"/>
<path d="M9 14l6-6 5 5-6 6"/>
</svg>`;
}

// ---- mock content ---------------------------------------------------------

function NoCaptureState() {
  return (
    <div
      data-testid="annotation-empty-state"
      style={{
        width: '100%',
        height: '100%',
        background: 'var(--bg-muted)',
        border: '1px dashed var(--border)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        color: 'var(--text-tertiary)',
        userSelect: 'none',
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 500 }}>No capture attached</div>
      <div style={{ fontSize: 12, maxWidth: 360, textAlign: 'center', lineHeight: 1.5 }}>
        Capture this page from the Toado extension, or click the
        <strong> + </strong>below to upload an image.
      </div>
    </div>
  );
}
